import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  MapPin, CheckCircle2, Phone, Star, Clock, Calendar, IndianRupee, ChevronRight, ChevronLeft, Flame,
  User, Mail, MonitorSmartphone, Layers, Target, Lightbulb, Check, Zap, BadgeCheck,
  Users, FlaskConical, Award, Briefcase, Monitor, ClipboardCheck, Sparkles, Download, ChevronDown, ArrowRight,
  GraduationCap, TrendingUp, Linkedin, Eye, MessageCircle,
} from "lucide-react";
import api from "@/lib/api";
import { getAttribution } from "@/lib/analytics";
import { useSEO, JsonLd } from "@/lib/seo";
import { toast } from "sonner";
import VenueBookingForm from "@/components/VenueBookingForm";
import ContactPage from "@/components/ContactPage";
import VenuePage from "@/components/VenuePage";
import MissionPage from "@/components/MissionPage";
import TestimonialsPage from "@/components/TestimonialsPage";

const ICONS = {
  users: Users,
  "flask-conical": FlaskConical,
  award: Award,
  briefcase: Briefcase,
  monitor: Monitor,
  "clipboard-check": ClipboardCheck,
};

const DEFAULT_PHONE = "+91 9015 887 887";
const DEFAULT_ORDER = ["hero", "glance", "tabs", "overview", "roadmap", "spec_tracks", "tracks", "courses", "why_enroll", "journey", "instructors", "alumni_logos", "certification", "highlights", "curriculum", "who", "placement", "stories", "placed", "centre", "contact_strip", "faqs", "final_cta", "also_see"];
const ABOUT_ICONS = [GraduationCap, Monitor, BadgeCheck, Briefcase];

const OVERVIEW_V2 = ["/ccna-training-noida", "/agentic-ai-course-noida", "/aws-architect", "/tableau-course", "/tableau-software-course-noida", "/tableau-developer-course-greater-noida", "/data-analytics-course-noida", "/gen-ai-course-noida", "/machine-learning-with-python", "/azure-training", "/devops-training-noida", "/cloud-computing-courses-noida", "/web-development-courses-noida", "/full-stack-developer-course-ghaziabad", "/cloud-computing-certification-greater-noida", "/redhat-training-noida", "/python-training-noida", "/full-stack-developer-course-noida", "/java-training-noida", "/django", "/web-development", "/cloud-computing-classes-ghaziabad", "/mern-stack-course-noida", "/summer-training", "/ccna-course-ghaziabad", "/data-science-course-and-data-analytics-course", "/alteryx-advanced-certification-course-online-offline", "/ccnp-certification-noida", "/power-bi-course", "/power-bi-certification-noida", "/power-bi-certification-greater-noida", "/power-bi-certification-ghaziabad", "/digital-marketing-course-noida", "/sap-fico", "/ethical-hacking"];

const beautifyOverview = (html) => {
  try {
    const doc = new DOMParser().parseFromString(`<div id="lp-root">${html}</div>`, "text/html");
    const root = doc.getElementById("lp-root");
    if (!root) return html;
    const el = (n) => n && n.nodeType === 1;
    const txt = (n) => (n.textContent || "").trim();
    const kids = [...root.childNodes].filter((n) =>
      (el(n) && !["SCRIPT", "STYLE"].includes(n.tagName)) || (n.nodeType === 3 && n.textContent.trim()));
    const isStatH = (n) => el(n) && n.tagName === "H2" && txt(n).length > 0 && txt(n).length <= 30;
    const isStatL = (n) => el(n) && n.tagName === "P" && txt(n).length > 0 && txt(n).length <= 40 && !n.querySelector("img");
    const isCheckImg = (n) => el(n) && (n.tagName === "IMG" || (["P", "DIV"].includes(n.tagName) && !txt(n) && n.querySelector("img")));
    const isShortBold = (n) => el(n) && ((n.tagName === "P" && n.querySelector("strong,b")) || ["H3", "H4", "H5"].includes(n.tagName)) && txt(n).length > 2 && txt(n).length <= 60 && !n.querySelector("img");
    const isSpacer = (n) => el(n) && ["P", "DIV", "SPAN"].includes(n.tagName) && !txt(n) && !n.querySelector("img,br,iframe,video");
    const smallW = (img) => parseInt((img && img.getAttribute("width")) || "999", 10) <= 100;
    const isSmallImg = (n) => el(n) && n.tagName === "IMG" && smallW(n);
    const isSmallImgWrap = (n) => el(n) && n.tagName === "P" && !txt(n) && n.querySelector("img") && smallW(n.querySelector("img"));
    const out = [];
    let i = 0;
    while (i < kids.length) {
      const n = kids[i];
      if (isSpacer(n)) { i += 1; continue; }
      if (el(n) && ["UL", "OL"].includes(n.tagName)) {
        const lis = [...n.children].filter((c) => c.tagName === "LI");
        if (lis.length >= 4 && lis.every((li) => txt(li).length > 0 && txt(li).length <= 45 && !li.querySelector("img,ul,ol"))) n.classList.add("lp-list-cols");
        out.push(n); i += 1; continue;
      }
      if (isSmallImg(n) || isSmallImgWrap(n)) {
        let j = i; const av = [];
        while (j < kids.length && (isSmallImg(kids[j]) || isSmallImgWrap(kids[j]))) { av.push(kids[j]); j += 1; }
        if (av.length >= 3) {
          const row = doc.createElement("div"); row.className = "lp-avatars";
          av.forEach((x) => row.appendChild(x));
          if (el(kids[j]) && kids[j].tagName === "P" && txt(kids[j]).length > 2 && txt(kids[j]).length <= 60) { row.appendChild(kids[j]); j += 1; }
          out.push(row); i = j; continue;
        }
      }
      if (isStatH(n) && isStatL(kids[i + 1])) {
        const cells = [];
        let j = i;
        while (j + 1 < kids.length && isStatH(kids[j]) && isStatL(kids[j + 1])) { cells.push([kids[j], kids[j + 1]]); j += 2; }
        if (cells.length >= 2) {
          const band = doc.createElement("div"); band.className = "lp-stats";
          cells.forEach(([h, p]) => { const c = doc.createElement("div"); c.className = "lp-stat"; c.appendChild(h); c.appendChild(p); band.appendChild(c); });
          out.push(band); i = j; continue;
        }
      }
      const readItem = (k) => {
        if (isCheckImg(kids[k]) && el(kids[k + 1]) && kids[k + 1].tagName === "P" && txt(kids[k + 1]).length > 2 && txt(kids[k + 1]).length <= 90) return 2;
        if (isShortBold(kids[k])) return 1;
        return 0;
      };
      let j = i; const items = [];
      while (j < kids.length) { const step = readItem(j); if (!step) break; items.push(step === 2 ? [kids[j], kids[j + 1]] : [kids[j]]); j += step; }
      if (items.length >= 3) {
        const grid = doc.createElement("div"); grid.className = "lp-checks";
        items.forEach((nodes) => { const c = doc.createElement("div"); c.className = "lp-check"; nodes.forEach((x) => c.appendChild(x)); grid.appendChild(c); });
        out.push(grid); i = j; continue;
      }
      out.push(n); i += 1;
    }
    root.innerHTML = "";
    out.forEach((n) => root.appendChild(n));
    return root.innerHTML;
  } catch { return html; }
};

const PartnerLogo = ({ p, i }) => {
  const [failed, setFailed] = useState(false);
  const inner = p.image && !failed ? (
    <img src={p.image} alt={p.name} loading="lazy" onError={() => setFailed(true)}
      className="h-10 w-auto max-w-[150px] object-contain grayscale opacity-80 hover:grayscale-0 hover:opacity-100 transition-all duration-300" />
  ) : (
    <span className="text-base font-extrabold tracking-tight text-slate-700">{p.name}</span>
  );
  return p.url ? (
    <a href={p.url} target="_blank" rel="noopener noreferrer" className="inline-flex hover:opacity-80 transition-opacity" data-testid={`about-partner-${i}`}>{inner}</a>
  ) : (
    <span className="inline-flex" data-testid={`about-partner-${i}`}>{inner}</span>
  );
};

function AboutContactForm({ lp, subtitle }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !/^\d{10}$/.test(form.phone.replace(/\D/g, "").slice(-10))) {
      toast.error("Please enter your name and a valid 10-digit mobile number");
      return;
    }
    setSending(true);
    try {
      await api.post("/callbacks", {
        name: form.name.trim(),
        phone: form.phone.replace(/\D/g, "").slice(-10),
        email: form.email.trim(),
        message: form.message.trim(),
        program_slug: "",
        preferred_time: "Anytime",
        attribution: { ...getAttribution(), source: "landing_page_contact", path: lp.url_path, page: lp.url_path },
      });
      setSent(true);
    } catch {
      toast.error("Could not submit — please try again");
    } finally {
      setSending(false);
    }
  };
  if (sent) {
    return (
      <div className="bg-white rounded-2xl p-7 text-center" data-testid="about-contact-success">
        <CheckCircle2 className="w-10 h-10 text-emerald-500 mx-auto" />
        <p className="m-0 mt-3 text-lg font-extrabold text-slate-900">Request received!</p>
        <p className="m-0 mt-1 text-sm text-slate-500">Our counsellor will call you back shortly.</p>
      </div>
    );
  }
  const fld = "w-full min-h-[46px] px-4 rounded-xl border border-slate-200 bg-white text-[15px] text-slate-900 outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-200 transition-all";
  return (
    <form onSubmit={submit} className="bg-white rounded-2xl p-6 sm:p-7 flex flex-col gap-3.5" data-testid="about-contact-form">
      {subtitle && <p className="m-0 text-base font-extrabold text-slate-900">{subtitle}</p>}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
        <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Your Name" required className={fld} data-testid="about-contact-name" />
        <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Your Email" className={fld} data-testid="about-contact-email" />
      </div>
      <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Your Phone Number" required className={fld} data-testid="about-contact-phone-input" />
      <textarea rows={3} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Your Message" className={fld + " resize-y"} data-testid="about-contact-message" />
      <button type="submit" disabled={sending} className="mt-1 inline-flex items-center justify-center gap-2 rounded-xl bg-amber-400 hover:bg-amber-500 text-[#0A0F2B] font-bold px-6 py-3 text-sm transition-colors disabled:opacity-50" data-testid="about-contact-submit">
        {sending ? "Submitting…" : "Submit Now"}
      </button>
    </form>
  );
}

const CONTACT_MARKER = /<p[^>]*>\s*Fill out this form and we will get back to you\s*<\/p>/i;

function ContactBlockForm({ lp }) {
  const [form, setForm] = useState({ name: "", phone: "", email: "", mode: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !/^\d{10}$/.test(form.phone.replace(/\D/g, "").slice(-10))) {
      toast.error("Please enter your name and a valid 10-digit mobile number");
      return;
    }
    setSending(true);
    try {
      await api.post("/callbacks", {
        name: form.name.trim(),
        phone: form.phone.replace(/\D/g, "").slice(-10),
        email: form.email.trim(),
        program_slug: lp.program_slug || "",
        preferred_time: "Anytime",
        attribution: { ...getAttribution(), source: "landing_page_contact", path: lp.url_path, city: lp.city || "", preferred_mode: form.mode || "" },
      });
      setSent(true);
    } catch {
      toast.error("Could not submit — please try again");
    } finally {
      setSending(false);
    }
  };
  if (sent) {
    return (
      <div className="my-6 rounded-2xl border border-slate-200/80 bg-white p-6 text-center shadow-[0_4px_20px_-4px_rgba(15,23,42,0.06)]" data-testid="landing-contact-success">
        <CheckCircle2 className="w-10 h-10 text-amber-600 mx-auto" />
        <p className="m-0 mt-3 text-lg font-extrabold text-slate-900">Request received!</p>
        <p className="m-0 mt-1 text-sm text-slate-500">Our counsellor will call you back shortly.</p>
      </div>
    );
  }
  return (
    <form onSubmit={submit} className="my-6 rounded-2xl border border-slate-200/80 bg-white p-6 shadow-[0_4px_20px_-4px_rgba(15,23,42,0.06)] flex flex-col gap-3 max-w-[520px]" data-testid="landing-contact-form">
      <div className="grid sm:grid-cols-2 gap-3">
        <label className="flex flex-col gap-1.5 text-[13px] font-bold text-slate-700">Full name
          <input required placeholder="Your name" value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="min-h-[46px] px-4 border border-slate-300 rounded-xl font-medium text-[15px] bg-slate-50 outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-200 transition-all"
            data-testid="landing-contact-name" />
        </label>
        <label className="flex flex-col gap-1.5 text-[13px] font-bold text-slate-700">Mobile number
          <input required type="tel" placeholder="+91" value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
            className="min-h-[46px] px-4 border border-slate-300 rounded-xl font-medium text-[15px] bg-slate-50 outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-200 transition-all"
            data-testid="landing-contact-phone" />
        </label>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <label className="flex flex-col gap-1.5 text-[13px] font-bold text-slate-700">Email
          <input type="email" placeholder="you@example.com" value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            className="min-h-[46px] px-4 border border-slate-300 rounded-xl font-medium text-[15px] bg-slate-50 outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-200 transition-all"
            data-testid="landing-contact-email" />
        </label>
        <label className="flex flex-col gap-1.5 text-[13px] font-bold text-slate-700">Preferred mode
          <select value={form.mode} onChange={(e) => setForm({ ...form, mode: e.target.value })}
            className="min-h-[46px] px-3.5 border border-slate-300 rounded-xl font-medium text-[15px] bg-slate-50 outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-200 transition-all"
            data-testid="landing-contact-mode">
            <option value="">Any mode</option>
            <option>Live online</option>
            <option>Classroom</option>
            <option>Blended</option>
          </select>
        </label>
      </div>
      <button type="submit" disabled={sending}
        className="min-h-[50px] rounded-full bg-[#F4C244] hover:bg-[#ecb733] active:scale-[.98] text-slate-900 font-extrabold text-base transition-all disabled:opacity-60"
        data-testid="landing-contact-submit">
        {sending ? "Submitting…" : "Request a call back"}
      </button>
    </form>
  );
}

const titleCase = (s = "") => s.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

const PLACED_TRIPLET = /(<img\b[^>]*?>)\s*<h5>([^<]+)<\/h5>\s*([^<]+?)(?=<img|<h2|$)/gis;

// Extracts a legacy "Why enroll for ..." block (decorative arrow svgs + round icon images with
// alt text + bare text rows, trailing "Enroll Now!" CTA) into native icon-and-text row data.
const extractWhyEnroll = (html) => {
  const none = { html, whyEnroll: null };
  const cut = sliceH2(html, /Why enroll/i);
  if (!cut) return none;
  const items = [];
  cut.seg.split(/<img\b/i).slice(1).forEach((part) => {
    const tag = "<img " + part;
    const src = imgSrc(tag);
    const alt = (tag.match(/alt=["']([^"']*)["']/i) || [])[1] || "";
    if (!src || !alt.trim()) return; // skip decorative arrow graphics (no alt)
    const rest = part.replace(/^[^>]*>/, "");
    const text = stripTags(rest).split(/enroll now!?/i)[0].trim();
    const w = parseInt((tag.match(/width=["']?(\d+)/i) || [])[1] || "0", 10);
    if (text.length > 20) items.push({ src, alt: alt.trim(), text, big: w > 200 });
  });
  if (!items.length) return none;
  const cta = /enroll now!?/i.test(cut.seg) ? "Enroll Now" : "";
  return { html: cut.html, whyEnroll: { title: stripTags(cut.title), items, cta } };
};

// Lifts the first "Course Overview" h2 paragraph into the hero as intro text (V2 pages without a glance block).
const extractOverviewIntro = (html) => {
  const none = { html, intro: "", title: "", meta: "", cta: "", badge: "", enquire: "" };
  if (!html) return none;
  const h2s = [...html.matchAll(/<h2[^>]*>[\s\S]*?<\/h2>/gi)];
  const hm = h2s.find((m) => /course overview/i.test(m[0]));
  if (!hm) return none;
  const start = hm.index + hm[0].length;
  const rest = html.slice(start);
  const nxt = rest.search(/<h2|<img/i);
  const seg = rest.slice(0, nxt > 0 ? nxt : rest.length);
  const intro = stripTags(seg).split(/book your free demo|download (our )?curriculum|use the form/i)[0].trim();
  if (intro.length < 60) return none;
  const title = stripTags(hm[0]);
  const h4m = seg.match(/<h4[^>]*>([\s\S]*?)<\/h4>/i);
  const meta = h4m ? stripTags(h4m[1]) : "";
  const ctaM = stripTags(seg).match(/book your free demo( class)?( now)?!?/i);
  const cta = ctaM ? ctaM[0].replace(/\s+/g, " ").trim() : "";
  // Lift a certification/badge image sitting immediately before the overview h2 into the header.
  let before = html.slice(0, hm.index);
  let badge = "";
  const bm = before.match(/(<img\b[^>]*>)\s*$/i);
  if (bm) {
    const alt = (bm[1].match(/alt=["']([^"']*)["']/i) || [])[1] || "";
    const src = imgSrc(bm[1]);
    if (src && alt.trim()) { badge = src; before = before.slice(0, before.length - bm[0].length); }
  }
  // Lift a lone bare CTA line (e.g. "Enquire Now!") that only decorates the same lead-in.
  let enquire = "";
  const bt = stripTags(before);
  if (bt && /^(enquire|enroll|apply)( now)?!?$/i.test(bt) && !/<img\b/i.test(before)) { enquire = bt; before = ""; }
  return { html: before + html.slice(start + seg.length), intro, title, meta, cta, badge, enquire };
};

// Extracts legacy summer-training track cards (img + h6 duration + h6 language + h4 name +
// star + "4.7 8400 learners Know More" + chevron) into native course-card grid data.
// Self-guarded: fires only on 3+ cards forming one consecutive run.
const extractTrackCards = (html) => {
  const none = { html, tracks: null };
  if (!html) return none;
  const matches = [...html.matchAll(/<img\b[^>]*?\/>\s*<h6[^>]*>([\s\S]*?)<\/h6>\s*<h6[^>]*>([\s\S]*?)<\/h6>\s*<h4[^>]*>([\s\S]*?)<\/h4>\s*<img\b[^>]*?\/>\s*([\d.]+)\s+([\d,]+)\s+learners\s+Know More\s*<img\b[^>]*?\/>/gi)];
  if (matches.length < 3) return none;
  for (let i = 1; i < matches.length; i++) {
    if (html.slice(matches[i - 1].index + matches[i - 1][0].length, matches[i].index).trim()) return none; // must be one consecutive run
  }
  const start = matches[0].index;
  const end = matches[matches.length - 1].index + matches[matches.length - 1][0].length;
  const cards = matches.map((m) => {
    const name = stripTags(m[3]);
    return {
      src: imgSrc(m[0]),
      duration: stripTags(m[1]),
      language: stripTags(m[2]),
      name,
      rating: m[4],
      learners: m[5],
      branch: /entrepreneurship|digital marketing|aedm/i.test(name) ? "BBA/MBA" : "CS/IT",
    };
  }).filter((cd) => cd.src && cd.name);
  if (cards.length < 3) return none;
  // capture the branch tagline sitting immediately before the first card
  let before = html.slice(0, start);
  let branchLine = "";
  const tailM = before.match(/([^<>]*(?:CS\s*\/\s*IT|BBA|MBA|Civil)[^<>]*)\s*$/i);
  if (tailM && tailM[1].trim().length >= 8 && tailM[1].trim().length <= 90) {
    branchLine = stripTags(tailM[1]);
    before = before.slice(0, before.length - tailM[0].length);
  }
  return { html: before + html.slice(end), tracks: { branchLine, cards } };
};

// Extracts the legacy "Browse our <Brand> Courses" block (h2 + 1-2 course cards: banner img +
// duration/language h6 pair + h4 name + star rating + "Know More" chevron) into a styled showcase
// grid. Runs after extractTrackCards, which owns the 3+ consecutive-card runs, so this only claims
// the small showcases track cards skip. Self-guarded: needs the h2 anchor plus a full card match.
const extractCourseShowcase = (html) => {
  const none = { html, courses: null };
  if (!html) return none;
  const h2M = /<h2[^>]*>\s*Browse our\s+([\s\S]*?)<\/h2>/i.exec(html);
  if (!h2M) return none;
  const nextH2 = html.indexOf("<h2", h2M.index + h2M[0].length);
  const seg = html.slice(h2M.index + h2M[0].length, nextH2 > 0 ? nextH2 : html.length);
  const cardRe = /<img\b[^>]*?\/>\s*<h6[^>]*>([\s\S]*?)<\/h6>\s*<h6[^>]*>([\s\S]*?)<\/h6>\s*<h4[^>]*>([\s\S]*?)<\/h4>\s*<img\b[^>]*?\/>\s*([\d.]+)\s+([\d,]+)\s+learners\s+Know More\s*<img\b[^>]*?\/>/gi;
  const matches = [...seg.matchAll(cardRe)];
  if (!matches.length) {
    // Orphaned "Browse our ..." heading with no cards under it (migration leftover) — drop it
    // only when nothing meaningful sits between it and the next h2.
    const leftover = stripTags(seg).replace(/[^\x20-\x7E]/g, "").trim();
    if (leftover) return none;
    return { html: html.slice(0, h2M.index) + html.slice(nextH2 > 0 ? nextH2 : html.length), courses: null };
  }
  const cards = matches.map((m) => ({
    src: imgSrc(m[0]),
    duration: stripTags(m[1]),
    language: stripTags(m[2]),
    name: stripTags(m[3]),
    rating: m[4],
    learners: m[5],
  })).filter((cd) => cd.src && cd.name);
  if (!cards.length) return none;
  const brand = stripTags(h2M[1]).replace(/courses?/i, "").replace(/[^\x20-\x7E]/g, "").replace(/\s+/g, " ").trim();
  const cutEnd = h2M.index + h2M[0].length + matches[matches.length - 1].index + matches[matches.length - 1][0].length;
  return { html: html.slice(0, h2M.index) + html.slice(cutEnd), courses: { brand, cards } };
};

// Extracts the legacy "MODULE N + Week range + Topics" curriculum blob (Webflow desktop set +
// interleaved mobile duplicate) into a native roadmap. Self-guarded: every meaningful fragment
// must be captured (case-insensitive duplicate sets collapse); "Tab 1 Tab 2 Tab 3" is a tab-nav artifact.
const extractModuleTimeline = (html) => {
  const none = { html, roadmap: null };
  if (!html) return none;
  const startM = html.search(/Tab 1 Tab 2 Tab 3|MODULE\s+1\s|Module\s+1\s/i);
  if (startM < 0) return none;
  const nextH2 = html.indexOf("<h2", startM);
  const end = nextH2 > 0 ? nextH2 : html.length;
  const seg = html.slice(startM, end);
  const modRe = /(?:MODULE|Module)\s*(\d+)\s*[-–:]?\s*([^<]+?)\s*<img\b[^>]*?\/>\s*(Week\s*\d+\s*[-–]\s*\d+)\s*<h4[^>]*>\s*Topics\s*<\/h4>\s*<ul[^>]*>([\s\S]*?)<\/ul>/gi;
  const byNum = new Map();
  for (const m of seg.matchAll(modRe)) {
    const topics = [...m[4].matchAll(/<li[^>]*>([\s\S]*?)<\/li>/gi)].map((x) => stripTags(x[1])).filter(Boolean);
    byNum.set(m[1], { num: m[1], name: stripTags(m[2]), weeks: m[3].replace(/\s+/g, " ").trim(), topics });
  }
  // The tab-layout header row (desktop) carries the authoritative module names, but its
  // headers precede the panels, so full matches can cross a header with the wrong topic list
  // or inherit a mobile-duplicate name. Parse header pairs on their own (first occurrence
  // wins) and overlay them onto the per-module topic lists.
  const headRe = /(?:MODULE|Module)\s*(\d+)\s*[-–:]?\s*([^<]+?)\s*<img\b[^>]*?\/>\s*(Week\s*\d+\s*[-–]\s*\d+)/gi;
  const headByNum = new Map();
  for (const m of seg.matchAll(headRe)) {
    if (!headByNum.has(m[1])) headByNum.set(m[1], { name: stripTags(m[2]), weeks: m[3].replace(/\s+/g, " ").trim() });
  }
  const modules = [...byNum.values()]
    .map((md) => {
      const h = headByNum.get(md.num);
      return h ? { ...md, name: h.name, weeks: h.weeks } : md;
    })
    .sort((a, b) => Number(a.num) - Number(b.num));
  if (modules.length < 3) return none;
  const captured = (modules.flatMap((md) => [md.name, ...md.topics]).join(" ")).toLowerCase();
  const frags = seg.split(/<[^>]+>/).map((t) => stripTags(t)).filter(Boolean);
  const fragOk = (f) => {
    let r = f.toLowerCase()
      .replace(/tab\s*\d+/g, " ").replace(/module\s*\d+/g, " ").replace(/week\s*\d+\s*[-–]\s*\d+/g, " ")
      .replace(/explore curriculum/g, " ").replace(/topics/g, " ").replace(/\s+/g, " ").trim();
    return !r || r.length < 8 || captured.includes(r);
  };
  if (frags.some((f) => f.length >= 8 && !fragOk(f))) return none;
  return { html: html.slice(0, startM) + html.slice(end), roadmap: { modules } };
};

// Extracts the legacy "Specialization Track" / "Career Track" h2 blocks (duration chip + labels
// + topics + career outcomes; career side has duplicated topic lists + webflow badge links).
// Self-guarded; duplicated lists collapse case-insensitively.
const extractSpecTracks = (html) => {
  const none = { html, specTracks: null };
  if (!html) return none;
  const cutA = sliceH2(html, /<h2[^>]*>\s*Specialization Track\s*<\/h2>/i);
  if (!cutA) return none;
  const sH5 = cutA.seg.match(/<h5[^>]*>([\s\S]*?)<\/h5>/i);
  const sLabels = [...cutA.seg.matchAll(/<h3[^>]*>([\s\S]*?)<\/h3>/gi)].map((m) => stripTags(m[1])).filter(Boolean);
  const sUl = cutA.seg.match(/<ul[^>]*>([\s\S]*?)<\/ul>/i);
  const sTopics = sUl ? [...sUl[1].matchAll(/<li[^>]*>([\s\S]*?)<\/li>/gi)].map((m) => stripTags(m[1])).filter(Boolean) : [];
  const sOutcome = sUl ? stripTags(cutA.seg.slice(cutA.seg.indexOf(sUl[0]) + sUl[0].length)) : "";
  const spec = { title: "Specialization Track", duration: sH5 ? stripTags(sH5[1]) : "", labels: sLabels, topics: sTopics, outcome: sOutcome };
  const specCap = [spec.title, spec.duration, ...spec.labels, ...spec.topics, spec.outcome].join(" ").toLowerCase();
  const sFrags = cutA.seg.split(/<[^>]+>/).map((t) => stripTags(t)).filter(Boolean);
  if (sFrags.some((f) => f.length >= 8 && !specCap.includes(f.toLowerCase()))) return none;
  let career = null;
  let outHtml = cutA.html;
  const cutB = sliceH2(cutA.html, /<h2[^>]*>\s*Career Track\s*<\/h2>/i);
  if (cutB) {
    const cH5 = cutB.seg.match(/<h5[^>]*>([\s\S]*?)<\/h5>/i);
    const cMods = [];
    for (const m of cutB.seg.matchAll(/(?:MODULE|Module)\s*(\d+)\s*[-–:]?\s*([^<]+?)\s*<img\b[^>]*?>\s*([^<]*?)(?=\s*(?:MODULE|Module|<))/gi)) {
      cMods.push({ num: m[1], name: stripTags(m[2]), dur: stripTags(m[3]) });
    }
    const seenUl = new Set();
    let cTopics = [];
    for (const u of cutB.seg.matchAll(/<ul[^>]*>([\s\S]*?)<\/ul>/gi)) {
      const items = [...u[1].matchAll(/<li[^>]*>([\s\S]*?)<\/li>/gi)].map((x) => stripTags(x[1])).filter(Boolean);
      const key = items.join("|").toLowerCase();
      if (items.length && !seenUl.has(key)) { seenUl.add(key); if (!cTopics.length) cTopics = items; }
    }
    career = { title: "Career Track", duration: cH5 ? stripTags(cH5[1]) : "", modules: cMods, topics: cTopics, explore: /explore curriculum/i.test(stripTags(cutB.seg)) ? "Explore Curriculum" : "" };
    const cCap = ([career.title, ...career.modules.map((m) => m.name), ...career.topics].join(" ")).toLowerCase();
    const cOk = (f) => {
      let r = f.toLowerCase()
        .replace(/module\s*\d+/g, " ").replace(/\d+\s*months?/g, " ")
        .replace(/explore curriculum/g, " ").replace(/topics/g, " ").replace(/\s+/g, " ").trim();
      return !r || r.length < 8 || cCap.includes(r);
    };
    const cFrags = cutB.seg.split(/<[^>]+>/).map((t) => stripTags(t)).filter(Boolean);
    if (cFrags.some((f) => f.length >= 8 && !cOk(f))) return none;
    outHtml = cutB.html;
  }
  if (!spec.topics.length && !spec.labels.length && !career) return none;
  return { html: outHtml, specTracks: { spec, career } };
};

// Drops purely decorative legacy graphics (empty-alt ellipse/glow/blur art) from the prose render.
// Images carry no text nodes, so text preservation is unaffected.
const dropDecorativeImgs = (html) => (html ? html.replace(/<img\b(?=[^>]*\bsrc=["'][^"']*(?:ellipse|glow|blur)[^"']*["'])(?![^>]*\balt=["'][^"']*[^\s"'])[^>]*>/gi, "") : html);

// Hides old-site nav artifacts from the React render: anchor-wrapped h2 links, a leading
// "Welcome to ..." h2, and the lowercase page-slug h2. Raw DB HTML stays untouched.
const cleanOverviewNav = (html, slug) => {
  if (!html) return html;
  let out = html.replace(/<a\b[^>]*>\s*<h2[^>]*>[\s\S]*?<\/h2>\s*<\/a>/gi, "");
  out = out.replace(/^\s*<h2[^>]*>\s*Welcome to[\s\S]*?<\/h2>/i, "");
  const name = (slug || "").replace(/^\//, "").replace(/-/g, " ").trim();
  if (name) out = out.replace(new RegExp(`<h2[^>]*>\\s*${name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\s*<\\/h2>`, "i"), "");
  return out;
};

// Extracts legacy "Recently Placed Students" blocks (img + h5 + run-on text) out of the
// prose HTML so the template renders them as a dedicated full-width slider section.
const extractPlaced = (html) => {
  const none = { html, placed: null };
  if (!html || !/Placed at:/i.test(html) || !/<h5>/i.test(html)) return none;
  const h2s = [...html.matchAll(/<h2[^>]*>[\s\S]*?<\/h2>/gi)];
  const h2m = h2s.find((m) => /Recently Placed Students/i.test(m[0]));
  if (!h2m) return none;
  const segStart = h2m.index;
  const nextH2 = html.indexOf("<h2", segStart + h2m[0].length);
  const segEnd = nextH2 > 0 ? nextH2 : html.length;
  const seg = html.slice(segStart, segEnd);
  const srcOf = (tag) => (tag.match(/src=["']([^"']+)["']/i) || [])[1] || "";
  const pm = seg.match(/<p[^>]*>([\s\S]*?)<\/p>/i);
  const intro = pm ? pm[1].replace(/<[^>]+>/g, "").replace(/\s+/g, " ").trim() : "";
  const seen = new Set();
  const students = [];
  seg.replace(PLACED_TRIPLET, (all, img, name, meta) => {
    const src = srcOf(img);
    const nm = name.trim();
    if (!src || seen.has(src + "|" + nm.toLowerCase())) return ""; // marquee-loop duplicates
    seen.add(src + "|" + nm.toLowerCase());
    const t = decodeEntities(String(meta).replace(/\s+/g, " ").trim());
    let role = t, company = "", course = "";
    const m = t.match(/\bPlaced at:\s*/i);
    if (m) {
      role = t.slice(0, m.index).replace(/[ .]+$/, "");
      const rest = t.slice(m.index + m[0].length);
      const cm = rest.match(/\bCourses?:\s*/i);
      if (cm) {
        company = rest.slice(0, cm.index).replace(/[ .]+$/, "");
        course = rest.slice(cm.index + cm[0].length).replace(/[ .]+$/, "");
      } else company = rest.replace(/[ .]+$/, "");
    }
    students.push({ src, name: nm, role, company, course });
    return "";
  });
  if (!students.length) return none;
  const title = h2m[0].replace(/<[^>]+>/g, "").trim() || "Recently Placed Students";
  return { html: html.slice(0, segStart) + html.slice(segEnd), placed: { title, intro, students } };
};

// Slices the segment under an <h2> matching `pattern` out of the prose HTML.
const sliceH2 = (html, pattern) => {
  if (!html) return null;
  const h2s = [...html.matchAll(/<h2[^>]*>[\s\S]*?<\/h2>/gi)];
  const h2m = h2s.find((m) => pattern.test(m[0]));
  if (!h2m) return null;
  const nextH2 = html.indexOf("<h2", h2m.index + h2m[0].length);
  const end = nextH2 > 0 ? nextH2 : html.length;
  return {
    html: html.slice(0, h2m.index) + html.slice(end),
    seg: html.slice(h2m.index, end),
    title: h2m[0].replace(/<[^>]+>/g, "").trim(),
  };
};

const imgSrc = (tag) => (tag.match(/src=["']([^"']+)["']/i) || [])[1] || "";
const decodeEntities = (s) => s.replace(/&nbsp;/g, " ").replace(/&quot;/g, '"').replace(/&#x27;|&#39;/g, "'").replace(/&lt;/g, "<").replace(/&gt;/g, ">").replace(/&amp;/g, "&");
const stripTags = (s) => decodeEntities(s.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").replace(/\s+([,.!?;:])/g, "$1").trim());
const formatStat = (v) => (/^\d{4,}$/.test(v) ? Number(v).toLocaleString("en-IN") : v);

// Extracts the leading "at-a-glance" block (mode subtitle, intro, icon chips, checklist,
// free-demo CTA, learner avatars, h2/p stat cards) so the hero can render it natively.
const extractHeroGlance = (html) => {
  const none = { html, glance: null };
  if (!html) return none;
  const pairRe = /<h2[^>]*>([\s\S]*?)<\/h2>\s*<p[^>]*>([\s\S]*?)<\/p>/gi;
  const run = [];
  let m, runStart = -1, prevEnd = -1;
  while ((m = pairRe.exec(html))) {
    const v = stripTags(m[1]);
    const l = stripTags(m[2]);
    const okPair = v.length > 0 && v.length <= 25 && l.length > 0 && l.length <= 30 && !/<img/i.test(m[1] + m[2]);
    const okGap = runStart < 0 ? m.index <= 3500 : html.slice(prevEnd, m.index).trim() === "";
    if (okPair && okGap) {
      if (runStart < 0) runStart = m.index;
      run.push([m[1], m[2]]);
      prevEnd = m.index + m[0].length;
    } else if (run.length) break;
  }
  if (run.length < 2) return none;
  const head = html.slice(0, runStart);
  if (!/<h3/i.test(head) && !/<ul/i.test(head) && (head.match(/<img\b/gi) || []).length < 3) return none;
  const h3m = head.match(/<h3[^>]*>([\s\S]*?)<\/h3>/i);
  const subtitle = h3m ? stripTags(h3m[1]) : "";
  const pms = [...head.matchAll(/<p[^>]*>([\s\S]*?)<\/p>/gi)];
  const introM = pms.find((p) => !/<img/i.test(p[1]) && !/satisfied learners/i.test(p[1]));
  const learnM = pms.find((p) => /satisfied learners/i.test(p[1]));
  const chips = [...head.matchAll(/(<img\b[^>]*?>)\s*<h5>([^<]+)<\/h5>/gi)]
    .map((c) => ({ icon: imgSrc(c[1]), label: decodeEntities(c[2].trim()) }))
    .filter((c) => c.label);
  const ulm = head.match(/<ul[^>]*>([\s\S]*?)<\/ul>/i);
  const highlights = ulm ? [...ulm[1].matchAll(/<li[^>]*>([\s\S]*?)<\/li>/gi)].map((li) => stripTags(li[1])).filter(Boolean) : [];
  const headText = stripTags(head);
  const ctaM = headText.match(/enquire now!?\s*download (our )?(brochure|curriculum)|apply for free demo( class)?( now)?!?|download (our )?(brochure|curriculum)( now)?!?|enquire now!?|enroll now!?|book (your )?free demo( now)?!?/i);
  const cta = ctaM ? ctaM[0].replace(/\s+/g, " ").trim() : "";
  const avatars = [...head.matchAll(/<img\b[^>]*?>/gi)]
    .filter((im) => {
      const w = parseInt((im[0].match(/width=["']?(\d+)/i) || [])[1] || "999", 10);
      const after = head.slice(im.index + im[0].length, im.index + im[0].length + 40);
      return w <= 100 && !/^\s*<h5/i.test(after);
    })
    .map((im) => imgSrc(im[0]))
    .filter(Boolean);
  const glance = {
    subtitle,
    intro: introM ? stripTags(introM[1]) : "",
    chips,
    highlights,
    cta,
    avatars,
    learnersText: learnM ? stripTags(learnM[1]) : "",
    stats: run.map(([v, l]) => ({ value: stripTags(v), label: stripTags(l) })),
  };
  if (!subtitle && !glance.intro && !chips.length && !highlights.length && avatars.length < 3) return none;
  return { html: html.slice(prevEnd), glance };
};


// Extracts the trailing "Claim your Free Demo" CTA block (h2 + desc + tel link +
// learner avatars + trusted-by text + demo image) into native CTA card data.
const extractDemoCta = (html) => {
  const none = { html, demoCta: null };
  if (!html) return none;
  const h2s = [...html.matchAll(/<h2[^>]*>[\s\S]*?<\/h2>/gi)];
  const first = h2s.find((m) => /Claim your|Demo Class/i.test(m[0]));
  if (!first) return none;
  // Merge consecutive demo-titled h2 blocks (legacy pages ship desktop+mobile duplicates).
  let end = html.length;
  let cursor = first.index + first[0].length;
  for (;;) {
    const ni = html.indexOf("<h2", cursor);
    if (ni < 0) break;
    const close = html.indexOf("</h2>", ni);
    const h2txt = close > 0 ? html.slice(ni, close + 5) : "";
    if (h2txt && /Claim your|Demo Class/i.test(h2txt)) { cursor = close + 5; continue; }
    end = ni;
    break;
  }
  const seg = html.slice(first.index, end);
  const title = stripTags(first[0]).replace(/[\u{1F000}-\u{1FAFF}\u{2600}-\u{27BF}\u{FE0F}]/gu, "").replace(/\s+/g, " ").trim();
  const pm = seg.match(/<p[^>]*>([\s\S]*?)<\/p>/i);
  let desc = pm ? stripTags(pm[1]) : "";
  if (!desc || desc.length < 40) {
    const frag = seg.replace(/<h2[^>]*>[\s\S]*?<\/h2>/gi, " ").replace(/<img\b[^>]*?>/gi, " ").replace(/<a\b[^>]*>[\s\S]*?<\/a>/gi, " ")
      .split(/<[^>]+>/).map((t) => stripTags(t)).find((t) => t.length >= 40);
    if (frag) desc = frag.split(/get your certificate|book your free demo|download (our )?curriculum/i)[0].trim();
  }
  const am = seg.match(/<a\b[^>]*href=["']tel:([^"']+)["'][^>]*>([\s\S]*?)<\/a>/i);
  const phone = am ? am[1] : "";
  const ctaLabel = am ? stripTags(am[2]).replace(/^[^\w\s]+\s*/, "").trim() : "";
  const tm = seg.match(/<p[^>]*>([^<]*Trusted by[^<]*)<\/p>/i);
  const trustedText = tm ? stripTags(tm[1]) : "";
  const avatars = [];
  let image = "";
  for (const im of seg.matchAll(/<img\b[^>]*?>/gi)) {
    const w = parseInt((im[0].match(/width=["']?(\d+)/i) || [])[1] || "999", 10);
    const src = imgSrc(im[0]);
    if (!src) continue;
    if (w <= 600) { if (avatars.length < 6) avatars.push(src); }
    else if (!image) image = src;
  }
  if (!desc && !trustedText) return none;
  const bm = stripTags(seg).match(/book (a|your) (free )?demo( class)?( now)?!?/i);
  const bookLabel = bm ? bm[0].replace(/\s+/g, " ").trim() : "";
  return { html: html.slice(0, first.index) + html.slice(end), demoCta: { title, desc, phone, ctaLabel, trustedText, avatars, image, bookLabel } };
};

// Extracts a legacy "CONTACT" helpline block into a compact navy strip. Self-guarded:
// fires only when every meaningful text fragment in the segment is captured.
const extractContactStrip = (html) => {
  const none = { html, contact: null };
  const cut = sliceH2(html, /<h2[^>]*>\s*CONTACT\s*<\/h2>/i);
  if (!cut) return none;
  const seg = cut.seg;
  const title = stripTags(cut.title);
  const tel = seg.match(/href=["']tel:([^"']+)["']/i);
  const phone = tel ? tel[1].replace(/[^+\d]/g, "") : "";
  const h6s = [...seg.matchAll(/<h6[^>]*>([\s\S]*?)<\/h6>/gi)].map((m) => stripTags(m[1])).filter(Boolean);
  const phoneLabel = h6s.find((t) => /call us/i.test(t)) || h6s[0] || "";
  const phoneDisplay = h6s.find((t) => t.replace(/\D/g, "").length >= 7) || "";
  const pm = seg.match(/<p[^>]*>([\s\S]*?)<\/p>/i);
  const note = pm ? stripTags(pm[1]) : "";
  const afterH2 = seg.replace(/<h2[^>]*>[\s\S]*?<\/h2>/i, "");
  const introM = afterH2.match(/^\s*([^<]+)/);
  const intro = introM ? stripTags(introM[1]) : "";
  const captured = [title, intro, note, phoneLabel, phoneDisplay].join(" ").toLowerCase();
  const frags = seg.split(/<[^>]+>/).map((t) => stripTags(t)).filter(Boolean);
  if (frags.some((f) => f.length >= 8 && !captured.includes(f.toLowerCase()))) return none;
  if (!phone && !note && !intro) return none;
  return { html: cut.html, contact: { title, intro, note, phone, phoneLabel, phoneDisplay } };
};

// Extracts legacy "Our Journey so far" stat triplets (img + number + h5) into a stat band.
const extractJourney = (html) => {
  const none = { html, journey: null };
  const cut = sliceH2(html, /Our Journey so far/i);
  if (!cut) return none;
  const stats = [];
  cut.seg.replace(/(<img\b[^>]*?>)\s*([^<]+?)\s*<h5>([^<]+)<\/h5>/gi, (all, img, num, label) => {
    const n = stripTags(num);
    if (n && label.trim()) stats.push({ src: imgSrc(img), value: n, label: label.trim() });
    return "";
  });
  if (!stats.length) {
    // Variant: img + h5 label + description text (agentic-style blob markup)
    cut.seg.replace(/(<img\b[^>]*?>)\s*<h5>([\s\S]*?)<\/h5>([\s\S]*?)(?=<img\b|<h2|$)/gi, (all, img, label, desc) => {
      const l = decodeEntities(String(label).trim());
      const d = String(desc).trim();
      if (l && stripTags(d)) stats.push({ src: imgSrc(img), value: "", label: l, desc: d });
      return "";
    });
  }
  if (!stats.length) return none;
  return { html: cut.html, journey: { title: cut.title, stats } };
};

// Extracts legacy "Meet Our Instructors" blocks (img + h3 + run-on text) into faculty cards.
const extractInstructors = (html) => {
  const none = { html, instructors: null };
  const cut = sliceH2(html, /Meet (Our Instructors|Your Mentors)/i);
  if (!cut) return none;
  const ps = [...cut.seg.matchAll(/<p[^>]*>([\s\S]*?)<\/p>/gi)].map((m) => stripTags(m[1])).filter(Boolean);
  const people = [];
  // Handles both legacy markups: <img><h3>Name</h3> meta  AND  <img> meta <h3>Name</h3>
  cut.seg.replace(/(<img\b[^>]*?>)([\s\S]*?)(?=<img\b|<p\b|<h2|$)/gi, (all, img, rest) => {
    const nm = rest.match(/<h3>([^<]+)<\/h3>/i);
    if (!nm) return "";
    const meta = stripTags(rest.replace(/<h3>[^<]*<\/h3>/i, ""));
    people.push({ src: imgSrc(img), name: decodeEntities(nm[1].trim()), meta });
    return "";
  });
  if (!people.length) {
    // Variant C: photo + <h6>Name</h6> + <h6>Meta</h6> rows.
    const reC = /<img\b[^>]*?\/>\s*<h6[^>]*>([\s\S]*?)<\/h6>\s*<h6[^>]*>([\s\S]*?)<\/h6>/gi;
    let mc;
    const seenC = new Set();
    while ((mc = reC.exec(cut.seg))) {
      const name = stripTags(mc[1]);
      const meta = stripTags(mc[2]);
      const src = imgSrc(mc[0]);
      if (src && name && name.length <= 40 && meta && !seenC.has(name.toLowerCase())) {
        seenC.add(name.toLowerCase());
        people.push({ src, name, meta });
      }
    }
  }
  if (!people.length) {
    // Variant B: numbered fact list (h6 01/02/...) + a single trainer photo named via img alt.
    const imgs = [...cut.seg.matchAll(/<img\b[^>]*?>/gi)];
    const facts = cut.seg.split(/<h6[^>]*>[\s\S]*?<\/h6>/gi).slice(1)
      .map((t) => stripTags(t.replace(/<img\b[^>]*?>/gi, ""))).filter((t) => t.length > 10);
    if (!imgs.length || facts.length < 2) return none;
    const alt = (imgs[0][0].match(/alt=["']([^"']+)["']/i) || [])[1] || "";
    const name = alt.split(",")[0].trim() || "Our Trainer";
    const h4m = cut.seg.match(/<h4[^>]*>([\s\S]*?)<\/h4>/i);
    return { html: cut.html, instructors: { title: cut.title, intro: h4m ? stripTags(h4m[1]) : "", outro: "", people: [{ src: imgSrc(imgs[0][0]), name, meta: facts.join(" ") }] } };
  }
  const [intro, outro] = ps.length > 1 ? [ps[0], ps[ps.length - 1]] : [ps[0] || "", ""];
  return { html: cut.html, instructors: { title: cut.title, intro, outro, people } };
};

// Extracts a legacy "Course Content" block (h2/h3 heading + h5 topic list) into curriculum module data.
const extractCurriculumBlob = (html) => {
  const none = { html, curriculum: null };
  const heads = [...html.matchAll(/<h[23][^>]*>[\s\S]*?<\/h[23]>/gi)];
  const hm = heads.find((m) => /Course Content|Curriculum/i.test(stripTags(m[0])));
  if (!hm) return none;
  const rest = html.slice(hm.index + hm[0].length);
  const nxt = rest.search(/<h[23]\b/i);
  const seg = rest.slice(0, nxt > 0 ? nxt : rest.length);
  const topics = [...seg.matchAll(/<h5[^>]*>([\s\S]*?)<\/h5>/gi)]
    .map((m) => decodeEntities(stripTags(m[1])).trim())
    .filter((t) => t.length > 1 && t.length <= 60);
  if (topics.length < 3) return none;
  const title = decodeEntities(stripTags(hm[0])).trim();
  const note = /Download Course Content/i.test(seg) ? "Download Course Content" : "";
  const newHtml = html.slice(0, hm.index) + html.slice(hm.index + hm[0].length + seg.length);
  return { html: newHtml, curriculum: { title, note, topics } };
};

// Extracts legacy "Success Stories" blocks (img + quote + name + Course - X) into story cards.
const extractStoriesBlob = (html) => {
  const none = { html, stories: null };
  const cut = sliceH2(html, /Success Stories|Alumni Speak|Testimonials/i);
  if (!cut) return none;
  const seen = new Set();
  const stories = [];
  let cur = null;
  const flush = () => {
    const key = cur && `${cur.name.toLowerCase()}|${cur.text.slice(0, 40).toLowerCase()}`;
    if (cur && cur.src && cur.text && cur.text.length >= 40 && cur.name && cur.name.length <= 40 && !seen.has(key)) {
      seen.add(key);
      stories.push(cur);
    }
    cur = null;
  };
  cut.seg.split(/<img\b/i).slice(1).forEach((part) => {
    const src = imgSrc("<img " + part);
    const rest = part.replace(/^[^>]*>/, "");
    const pMatch = rest.match(/<p[^>]*>([\s\S]*?)<\/p>/i);
    const plain = stripTags(rest).replace(/[\u200B\u200C\u200D]/g, " ").replace(/\s+/g, " ").trim();
    const courseM = plain.match(/Course\s*-\s*(.+)$/);
    if (cur && courseM && !pMatch) {
      // variant B continuation: small role icon img followed by "Course - X"
      cur.role = `Course - ${courseM[1].trim()}`;
      flush();
      return;
    }
    if (src && pMatch) {
      // variant A2: bare quote + name + "Course - X" with a trailing <p> (e.g. outro) in the same run
      const prePlain = stripTags(rest.slice(0, pMatch.index)).replace(/[​‍‌]/g, " ").replace(/\s+/g, " ").trim();
      const am = prePlain.match(/^([\s\S]*?)\s+([A-Z][A-Za-z]+(?:\s+[A-Z][A-Za-z]+){0,2})\s+Course\s*-\s*(.+)$/);
      if (am && am[1].trim().length >= 40) {
        flush();
        cur = { src, text: am[1].trim(), name: am[2].trim(), role: `Course - ${am[3].trim()}` };
        flush();
        return;
      }
      // variant B: photo + bare name + <p>quote</p>
      flush();
      cur = { src, name: stripTags(rest.slice(0, pMatch.index)).trim(), text: decodeEntities(stripTags(pMatch[1])).trim(), role: "" };
      return;
    }
    if (src && courseM) {
      // variant A: photo + quote + name + "Course - X" as one run of text
      const m = plain.match(/^([\s\S]*?)\s+([A-Z][A-Za-z]+(?:\s+[A-Z][A-Za-z]+){0,2})\s+Course\s*-\s*(.+)$/);
      if (!m) return;
      flush();
      cur = { src, text: m[1].trim(), name: m[2].trim(), role: `Course - ${m[3].trim()}` };
      flush();
    }
  });
  flush();
  if (!stories.length) return none;
  const firstImg = cut.seg.search(/<img\b/i);
  const headSeg = firstImg > 0 ? cut.seg.slice(0, firstImg) : "";
  const ipm = headSeg.match(/<p[^>]*>([\s\S]*?)<\/p>/i);
  const ctaM = stripTags(cut.seg).match(/register now!?/i);
  const lastImg = cut.seg.search(/<img\b(?:(?!<img\b)[\s\S])*$/i);
  const tailSeg = lastImg >= 0 ? cut.seg.slice(lastImg) : "";
  const tailIm = tailSeg.match(/<img\b[^>]*>([\s\S]*)$/i);
  const opm = tailIm ? [...tailIm[1].matchAll(/<p[^>]*>([\s\S]*?)<\/p>/gi)].pop() : null;
  return { html: cut.html, stories: { title: stripTags(cut.title), intro: ipm ? stripTags(ipm[1]) : "", items: stories, cta: ctaM ? "Register Now!" : "", outro: opm ? stripTags(opm[1]) : "" } };
};

// Extracts a legacy "FAQs" h2 block (h3 question + answer pairs) into accordion data.
const extractFaqsBlob = (html) => {
  const none = { html, faqs: null };
  const h2s = [...html.matchAll(/<h2[^>]*>[\s\S]*?<\/h2>/gi)];
  const h2m = h2s.find((m) => /^<h2[^>]*>\s*(?:<strong>)?\s*(FAQs?\b|Frequently Asked Questions)/i.test(m[0]));
  if (!h2m) return none;
  const nextH2 = html.indexOf("<h2", h2m.index + h2m[0].length);
  const end = nextH2 > 0 ? nextH2 : html.length;
  const seg = html.slice(h2m.index, end);
  let before = html.slice(0, h2m.index);
  before = before.replace(/(\s*<img\b(?=[^>]*faq)[^>]*>\s*)+$/i, ""); // drop orphaned FAQ banner images only
  const qms = [...seg.matchAll(/<h3[^>]*>[\s\S]*?<\/h3>|<h5[^>]*>\s*<strong>[\s\S]*?<\/strong>\s*<\/h5>/gi)].sort((a, b) => a.index - b.index);
  const faqs = [];
  qms.forEach((qm, i) => {
    const q = stripTags(qm[0]).replace(/[?\s]+$/, "").trim();
    const aStart = qm.index + qm[0].length;
    const aEnd = i + 1 < qms.length ? qms[i + 1].index : seg.length;
    const a = decodeEntities(seg.slice(aStart, aEnd)
      .replace(/<\/?h6[^>]*>/gi, " ")
      .replace(/<li[^>]*>/gi, " • ")
      .replace(/<[^>]+>/g, " ")).replace(/\s+/g, " ").replace(/^ •\s*/, "").trim();
    if (q.length > 8 && a.length > 10) faqs.push({ q: q + "?", a });
  });
  if (!faqs.length) return none;
  return { html: before + html.slice(end), faqs: { title: stripTags(h2m[0]), items: faqs } };
};

// Extracts a legacy "Certification" block (h2 + intro + check-icon perks + certificate
// image + CTA + h4 feature list) into a premium navy showcase section.
const extractCertification = (html) => {
  const none = { html, certification: null };
  const cut = sliceH2(html, /Certification/i);
  if (!cut) return none;
  const seg = cut.seg;
  const allP = [...seg.matchAll(/<p[^>]*>([\s\S]*?)<\/p>/gi)];
  const perkPIdx = new Set();
  const perks = [];
  const perkRe = /<img\b[^>]*?\/>\s*<p[^>]*>([\s\S]*?)<\/p>/gi;
  let pmk;
  while ((pmk = perkRe.exec(seg))) {
    const t = stripTags(pmk[1]);
    if (t && t.length > 5) {
      perks.push(t);
      const pi = allP.findIndex((m, i) => !perkPIdx.has(i) && m.index >= pmk.index && m.index <= pmk.index + pmk[0].length);
      if (pi >= 0) perkPIdx.add(pi);
    }
  }
  const introIdx = allP.length && !perkPIdx.has(0) ? 0 : -1;
  const intro = introIdx === 0 ? stripTags(allP[0][1]) : "";
  const extraParas = allP.filter((m, i) => i !== introIdx && !perkPIdx.has(i)).map((m) => stripTags(m[1])).filter(Boolean);
  let certImage = "";
  for (const im of seg.matchAll(/<img\b[^>]*?>/gi)) {
    const w = parseInt((im[0].match(/width=["']?(\d+)/i) || [])[1] || "999", 10);
    const src = imgSrc(im[0]);
    if (src && w > 200 && !certImage) certImage = src;
  }
  const frags = seg.split(/<[^>]+>/).map((t) => stripTags(t)).filter(Boolean);
  const ctaF = frags.find((f) => /^(apply\b|get (your )?certificate|enroll now)/i.test(f) && f.length <= 60);
  const cta = ctaF || "";
  const features = [...seg.matchAll(/<h4[^>]*>([\s\S]*?)<\/h4>/gi)]
    .map((m) => stripTags(m[1])).filter((t) => t.length > 3);
  const stats = [];
  const statRe = /<img\b[^>]*?\/>\s*([\d.,]+)?\s*(?:<h4[^>]*>([\s\S]*?)<\/h4>)?\s*<h5[^>]*>([\s\S]*?)<\/h5>/gi;
  let ms;
  while ((ms = statRe.exec(seg))) {
    const num = (ms[1] || "").trim();
    const suffix = ms[2] ? stripTags(ms[2]) : "";
    const label = stripTags(ms[3]);
    if (label) stats.push({ src: imgSrc(ms[0]), value: (num + suffix).trim(), label });
  }
  if (!intro && !perks.length && !certImage) return none;
  // Self-guard: fire only when every meaningful text fragment in the segment is captured.
  const captured = [cut.title, intro, ...extraParas, ...perks, cta, ...features, ...stats.map((s) => `${s.value} ${s.label}`)].join(" ").toLowerCase();
  if (frags.some((f) => f.length >= 8 && !captured.includes(f.toLowerCase()))) return none;
  return { html: cut.html, certification: { title: stripTags(cut.title), intro, extraParas, perks, cta, certImage, features, stats } };
};

// Extracts legacy "Our <Course> Alumni Work At" logo runs (Webflow marquee duplicates) into a logo strip.
const extractAlumniLogos = (html) => {
  const none = { html, alumniLogos: null };
  const cut = sliceH2(html, /Alumni Work At|Prominent Recruiters/i);
  if (!cut) return none;
  const pm = cut.seg.match(/<p[^>]*>([\s\S]*?)<\/p>/i);
  const intro = pm ? stripTags(pm[1]) : "";
  const seen = new Set();
  const logos = [];
  for (const m of cut.seg.matchAll(/<img\b[^>]*?>/gi)) {
    const src = imgSrc(m[0]);
    if (!src || seen.has(src)) continue;
    seen.add(src);
    const alt = (m[0].match(/alt=["']([^"']*)["']/i) || [])[1] || "";
    logos.push({ src, alt });
  }
  if (logos.length < 3) return none;
  const pAll = [...cut.seg.matchAll(/<p[^>]*>([\s\S]*?)<\/p>/gi)].map((x) => stripTags(x[1])).filter(Boolean);
  const outro = pAll.length > 1 ? pAll[pAll.length - 1] : "";
  const cta = /enroll now!?/i.test(stripTags(cut.seg)) ? "Enroll Now" : "";
  return { html: cut.html, alumniLogos: { title: cut.title, intro, outro, cta, logos } };
};

function PlacedSlider({ students }) {
  const trackRef = useRef(null);
  const [paused, setPaused] = useState(false);
  const step = () => {
    const el = trackRef.current;
    const card = el && el.querySelector(".lp-placed");
    return card ? card.offsetWidth + 16 : 296;
  };
  const scrollCards = (dir) => {
    const el = trackRef.current;
    if (!el) return;
    const atEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 8;
    if (dir > 0 && atEnd) el.scrollTo({ left: 0, behavior: "smooth" });
    else el.scrollBy({ left: dir * step() * 2, behavior: "smooth" });
  };
  useEffect(() => {
    if (paused || (typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches)) return undefined;
    const id = setInterval(() => scrollCards(1), 4000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paused]);
  return (
    <div className="relative mt-7" onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)} onBlur={() => setPaused(false)}>
      <div ref={trackRef} className="lp-slider-track flex gap-4 overflow-x-auto pb-2 pr-1" data-testid="landing-placed-track">
        {students.map((s, i) => (
          <div key={`${s.src}-${i}`} className="lp-placed lp-placed-slide" data-testid={`landing-placed-${i}`}>
            <img src={s.src} alt={s.name} loading="lazy" />
            <div className="lp-placed-info min-w-0">
              <h5>{s.name}</h5>
              <p>{s.role}</p>
              <p className="lp-placed-meta">Placed at {s.company}{s.course ? ` · ${s.course}` : ""}</p>
            </div>
          </div>
        ))}
      </div>
      <button type="button" aria-label="Previous placed students" onClick={() => scrollCards(-1)} data-testid="landing-placed-prev"
        className="hidden sm:flex absolute -left-5 top-1/2 -translate-y-1/2 z-10 w-11 h-11 rounded-full bg-white border border-slate-200 shadow-lg items-center justify-center text-slate-700 hover:bg-[#F4C244] hover:text-slate-900 hover:border-amber-400 active:scale-95 transition-all">
        <ChevronLeft className="w-5 h-5" />
      </button>
      <button type="button" aria-label="Next placed students" onClick={() => scrollCards(1)} data-testid="landing-placed-next"
        className="hidden sm:flex absolute -right-5 top-1/2 -translate-y-1/2 z-10 w-11 h-11 rounded-full bg-white border border-slate-200 shadow-lg items-center justify-center text-slate-700 hover:bg-[#F4C244] hover:text-slate-900 hover:border-amber-400 active:scale-95 transition-all">
        <ChevronRight className="w-5 h-5" />
      </button>
    </div>
  );
}

function LogoMarquee({ logos, light = false, testidPrefix = "landing-logo", label = "Company logos" }) {
  const base = Array.from({ length: Math.max(1, Math.ceil(8 / logos.length)) }).flatMap(() => logos);
  return (
    <div className="lp-marquee mt-5" aria-label={label}>
      <div className="lp-marquee-track">
        {[0, 1].map((half) => (
          <div key={half} className="lp-marquee-half" aria-hidden={half === 1}>
            {base.map((l, i) => {
              const src = typeof l === "string" ? l : l.src;
              const alt = typeof l === "string" ? "" : l.alt || "";
              return (
                <span key={`${half}-${i}`} className={`lp-marquee-item${light ? " lp-marquee-item--light" : ""}`} data-testid={half === 0 ? `${testidPrefix}-${i}` : undefined}>
                  <img src={src} alt={alt} />
                </span>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function LandingPage({ data }) {
  const lp = data;
  const prog = lp.program || null;
  const cityRec = lp.city_record || null;
  const phone = (cityRec?.phones && cityRec.phones[0]) || DEFAULT_PHONE;
  const phoneHref = `tel:${phone.replace(/\s/g, "")}`;

  const [form, setForm] = useState({ name: "", phone: "", email: "", mode: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  useSEO({
    title: lp.title,
    description: lp.meta_description,
    canonical: lp.canonical || lp.url_path,
    noindex: lp.robots === "noindex",
    image: lp.og_image || undefined,
  });

  useEffect(() => {
    api.post("/events", { event_type: "page_view", metadata: { path: lp.url_path, source: "landing" } }).catch(() => {});
  }, [lp.url_path]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !/^\d{10}$/.test(form.phone.replace(/\D/g, "").slice(-10))) {
      toast.error("Please enter your name and a valid 10-digit mobile number");
      return;
    }
    setSending(true);
    try {
      await api.post("/callbacks", {
        name: form.name.trim(),
        phone: form.phone.replace(/\D/g, "").slice(-10),
        email: form.email.trim(),
        program_slug: lp.program_slug || "",
        preferred_time: "Anytime",
        attribution: { ...getAttribution(), source: "landing_page", path: lp.url_path, city: lp.city || "", preferred_mode: form.mode || "" },
      });
      setSent(true);
    } catch {
      toast.error("Could not submit — please try again");
    } finally {
      setSending(false);
    }
  };

  // ---- template order + visibility ----
  const tplSecs = useMemo(() => {
    const base = lp.template_sections && lp.template_sections.length
      ? lp.template_sections.map((s) => ({ key: s.key, enabled: s.enabled !== false }))
      : DEFAULT_ORDER.map((k) => ({ key: k, enabled: true }));
    const have = new Set(base.map((s) => s.key));
    const out = [...base];
    for (const k of DEFAULT_ORDER) {
      if (have.has(k)) continue;
      const ins = { key: k, enabled: true };
      const AFTER = { placed: ["stories"], journey: ["overview"], instructors: ["journey", "overview"], alumni_logos: ["instructors", "journey", "overview"], certification: ["alumni_logos", "overview"], contact_strip: ["centre", "instructors"], tracks: ["overview"], courses: ["tracks", "overview"], roadmap: ["overview"], spec_tracks: ["roadmap", "overview"] };
      const anchors = AFTER[k];
      if (anchors) {
        let i = -1;
        for (const a of anchors) { i = out.findIndex((s) => s.key === a); if (i >= 0) break; }
        out.splice(i >= 0 ? i + 1 : out.length, 0, ins);
      } else out.push(ins);
    }
    return out;
  }, [lp.template_sections]);
  const enabled = new Set(tplSecs.filter((s) => s.enabled !== false).map((s) => s.key));
  const order = tplSecs.filter((s) => s.enabled !== false).map((s) => s.key);

  // ---- section data (empty sections hide) ----
  // Program highlights arrive either as rich {icon, title, text} objects or plain strings.
  const highlights = (prog?.highlights || []).map((h) => (typeof h === "string" ? { title: h, text: "", icon: "" } : h));
  const whoFor = prog?.who_for || [];
  const prereqs = prog?.prerequisites || [];
  const logos = prog?.company_logos || [];
  const alumniPhotos = lp.alumni_photos || [];
  const overviewV2 = OVERVIEW_V2.includes(lp.url_path);
  const { html: overviewHtml, placed: placedData, journey: journeyData, instructors: instructorsData, alumniLogos: alumniLogosData, blobStories, blobFaqs, blobCurr, glanceData, demoCta, overviewIntro, ovTitle, ovMeta, ovCta, ovBadge, ovEnquire, whyEnrollData, certData, contactData, trackData, roadmapData, specTracksData, courseData } = useMemo(() => {
    const raw = lp.overview_html || lp.body_html || "";
    const r0 = overviewV2 ? extractHeroGlance(raw) : { html: raw, glance: null };
    const r0b = overviewV2 && !(r0.glance && r0.glance.intro) ? extractOverviewIntro(r0.html) : { html: r0.html, intro: "", title: "", meta: "", cta: "", badge: "", enquire: "" };
    const r0c = overviewV2 ? extractTrackCards(r0b.html) : { html: r0b.html, tracks: null };
    const r0d = overviewV2 ? extractModuleTimeline(r0c.html) : { html: r0c.html, roadmap: null };
    const r0e = overviewV2 ? extractSpecTracks(r0d.html) : { html: r0d.html, specTracks: null };
    const r0f = overviewV2 ? extractCourseShowcase(r0e.html) : { html: r0e.html, courses: null };
    const r1 = extractPlaced(r0f.html);
    const r2 = extractJourney(r1.html);
    const r3 = extractInstructors(r2.html);
    const r4 = extractAlumniLogos(r3.html);
    const r4b = overviewV2 ? extractWhyEnroll(r4.html) : { html: r4.html, whyEnroll: null };
    const r4c = overviewV2 ? extractCertification(r4b.html) : { html: r4b.html, certification: null };
    const r5 = overviewV2 ? extractStoriesBlob(r4c.html) : { html: r4c.html, stories: null };
    const r6 = overviewV2 ? extractFaqsBlob(r5.html) : { html: r5.html, faqs: null };
    const r7 = overviewV2 && !(lp.program && (lp.program.curriculum_modules || []).length) ? extractCurriculumBlob(r6.html) : { html: r6.html, curriculum: null };
    const r8 = overviewV2 ? extractDemoCta(r7.html) : { html: r7.html, demoCta: null };
    const r9 = overviewV2 ? extractContactStrip(r8.html) : { html: r8.html, contact: null };
    const finalHtml = overviewV2 ? dropDecorativeImgs(cleanOverviewNav(r9.html, lp.url_path)) : r9.html;
    return { html: finalHtml, placed: r1.placed, journey: r2.journey, instructors: r3.instructors, alumniLogos: r4.alumniLogos, blobStories: r5.stories, blobFaqs: r6.faqs, blobCurr: r7.curriculum, glanceData: r0.glance, demoCta: r8.demoCta, overviewIntro: r0b.intro, ovTitle: r0b.title, ovMeta: r0b.meta, ovCta: r0b.cta, ovBadge: r0b.badge, ovEnquire: r0b.enquire, whyEnrollData: r4b.whyEnroll, certData: r4c.certification, contactData: r9.contact, trackData: r0c.tracks, roadmapData: r0d.roadmap, specTracksData: r0e.specTracks, courseData: r0f.courses };
  }, [lp.overview_html, lp.body_html, lp.program, lp.url_path, overviewV2]);
  const modules = (prog?.curriculum_modules && prog.curriculum_modules.length) ? prog.curriculum_modules : (blobCurr ? blobCurr.topics.map((t) => ({ title: t, description: "" })) : []);
  const stories = [...(lp.testimonials || []), ...(blobStories?.items || [])];
  const faqs = [...(lp.faqs || []), ...(lp.city_faqs || []), ...(blobFaqs?.items || [])];
  const contactSplit = useMemo(() => {
    const m = overviewHtml.match(CONTACT_MARKER);
    if (!m) return null;
    const cut = m.index + m[0].length;
    return [overviewHtml.slice(0, cut), overviewHtml.slice(cut)];
  }, [overviewHtml]);
  const bodyParts = useMemo(() => {
    const parts = contactSplit ? [contactSplit[0], contactSplit[1]] : [overviewHtml];
    return overviewV2 ? parts.map(beautifyOverview) : parts;
  }, [overviewHtml, contactSplit, overviewV2]);
  const alsoSee = lp.also_see || [];
  const domainName = prog?.category_slug ? titleCase(prog.category_slug) : "";
  const fee = prog?.tuition_fee ? `₹${Number(prog.tuition_fee).toLocaleString("en-IN")}` : "";
  const duration = prog?.duration_months ? `${prog.duration_months} months` : "";
  const mode = prog?.mode || "";
  const certification = prog?.certification || "";
  const nextBatch = lp.next_batch || "";
  const centreChip = cityRec?.has_centre ? cityRec.centre_name : cityRec ? "Live online + nearest centre" : "";

  const h1Text = lp.h1 || lp.title || "";
  const [h1Main, h1Accent] = useMemo(() => {
    const cities = [...new Set([lp.city, "Delhi NCR", "Greater Noida", "Ghaziabad", "Noida"].filter(Boolean))];
    for (const c of cities) {
      const i = h1Text.lastIndexOf(c);
      if (i > 0) return [h1Text.slice(0, i), h1Text.slice(i)];
    }
    return [h1Text, ""];
  }, [h1Text, lp.city]);

  const show = (key, hasData) => enabled.has(key) && hasData;
  const ovEnabled = (k) => (lp.section_overrides || []).some((o) => o.key === k && o.enabled === true);
  const aHero = lp.about_hero || {};
  const aIntro = lp.about_intro || {};
  const aStats = (lp.stats || []).filter((s) => s && s.value);
  const aTeam = (lp.team_members || []).filter((m) => m && m.name);
  const aPartners = (lp.partner_logos || []).filter((x) => x && x.name);
  const aMedia = (lp.media_cards || []).filter((m) => m && m.title);
  const aMV = lp.mission_vision || {};
  const aJourney = (lp.journey_items || []).filter((j) => j && j.year);
  const aContact = lp.contact_block || {};
  const aMaps = (lp.visit_maps || []).filter((m) => m && (m.label || m.address));
  const [trackBranch, setTrackBranch] = useState("all");
  const vis = {
    glance: show("glance", !!(prog && (duration || mode || fee || certification || nextBatch))),
    overview: show("overview", !!overviewHtml),
    roadmap: show("roadmap", !!(roadmapData && roadmapData.modules.length)),
    spec_tracks: show("spec_tracks", !!specTracksData),
    tracks: show("tracks", !!(trackData && trackData.cards.length)),
    courses: show("courses", !!(courseData && courseData.cards.length)),
    why_enroll: show("why_enroll", !!(whyEnrollData && whyEnrollData.items.length)),
    journey: show("journey", !!(journeyData && journeyData.stats.length)),
    instructors: show("instructors", !!(instructorsData && instructorsData.people.length)),
    alumni_logos: show("alumni_logos", !!(alumniLogosData && alumniLogosData.logos.length)),
    certification: show("certification", !!(certData && (certData.intro || (certData.perks || []).length || certData.certImage))),
    highlights: show("highlights", highlights.length > 0),
    curriculum: show("curriculum", modules.length > 0),
    who: show("who", whoFor.length > 0 || prereqs.length > 0),
    placement: show("placement", !!(prog?.placement_text || logos.length)),
    stories: show("stories", stories.length > 0 || alumniPhotos.length > 0),
    placed: show("placed", !!(placedData && placedData.students.length)),
    centre: show("centre", !!cityRec),
    contact_strip: show("contact_strip", !!contactData),
    faqs: show("faqs", faqs.length > 0),
    final_cta: enabled.has("final_cta"),
    also_see: show("also_see", alsoSee.length > 0),
    about_hero: show("about_hero", !!(lp.h1 || aHero.subtitle || aHero.intro || aHero.image || (aHero.icons || []).length)),
    about_intro: show("about_intro", !!(aIntro.title || (aIntro.paragraphs || []).length || aIntro.image)),
    stats_band: show("stats_band", aStats.length > 0),
    team_grid: show("team_grid", aTeam.length > 0),
    partner_logos: show("partner_logos", aPartners.length > 0),
    media_cards: show("media_cards", aMedia.length > 0),
    mission_vision: show("mission_vision", !!(aMV.title || aMV.mission_text || aMV.vision_text || aMV.image)),
    journey_timeline: show("journey_timeline", aJourney.length > 0),
    contact_block: show("contact_block", !!(aContact.title || aContact.phone)),
    visit_maps: show("visit_maps", aMaps.length > 0),
    venue_form: ovEnabled("venue_form"),
  };
  const showHero = enabled.has("hero"); // about-style pages swap the course hero for about_hero via section overrides
  const heroImg = lp.hero_image || "";
  const heroSecondary = !!vis.about_hero;
  const showTabs = enabled.has("tabs");

  const heroHasChips = !!(prog?.placement_text || modules.length > 0 || certification || mode || centreChip);
  const heroSparse = !lp.badge && !lp.hero_intro && !heroHasChips && alumniPhotos.length === 0 && !nextBatch && !glanceData && !overviewIntro;
  const glanceHasRating = (glanceData?.chips || []).some((c) => /^\d+(\.\d+)?$/.test(c.label));

  const TAB_ANCHOR = { overview: "overview", instructors: "instructors", highlights: "highlights", curriculum: "curriculum", who: "who", placement: "placement", stories: "stories", placed: "placed", centre: "centre", faqs: "faq" };
  const TAB_LABEL = { overview: "Overview", instructors: "Instructors", highlights: "Highlights", curriculum: "Curriculum", who: "Who it's for", placement: "Placement", stories: "Alumni stories", placed: "Placed students", centre: "Centre", faqs: "FAQs" };
  const tabs = order.filter((k) => TAB_ANCHOR[k] && vis[k]).map((k) => [TAB_ANCHOR[k], TAB_LABEL[k]]);

  // When the CMS hero already renders native stat tiles, drop overlapping program metrics
  // (e.g. a conflicting "Duration") so the two bands never contradict each other.
  let glanceMetrics = [
    [Clock, "Duration", duration],
    [Monitor, "Mode", mode],
    [IndianRupee, "Course fee", fee],
    [Award, "Certification", certification],
    [Calendar, "Next batch", nextBatch],
  ].filter((m) => m[2]);
  if ((glanceData?.stats || []).length >= 2 && glanceMetrics.length) {
    const heroStatLabels = new Set(glanceData.stats.map((s) => (s.label || "").toLowerCase()));
    glanceMetrics = glanceMetrics.filter((m) => !heroStatLabels.has(m[1].toLowerCase()));
  }

  const courseLd = prog
    ? {
        "@context": "https://schema.org",
        "@type": "Course",
        name: prog.title || lp.h1,
        description: lp.meta_description,
        provider: { "@type": "Organization", name: "Training Basket", sameAs: "https://www.trainingbasket.in" },
        url: `https://www.trainingbasket.in${lp.url_path}`,
      }
    : null;
  const faqLd = vis.faqs
    ? {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: faqs.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      }
    : null;

  const labelCls = "flex flex-col gap-1.5 text-[13px] font-bold text-slate-700";
  const fieldCls = "w-full min-h-[48px] pl-11 pr-4 border border-slate-300 rounded-2xl font-medium text-[15px] bg-slate-50 outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-200 transition-all";
  const fieldIconCls = "w-[18px] h-[18px] absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none";

  const sectionNodes = {
    about_hero: vis.about_hero && (
      <section key="about_hero" data-testid="about-hero" className="relative">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-14 items-center">
          <div className="min-w-0">
            <span className="inline-flex items-center gap-2.5 text-[11px] font-extrabold uppercase tracking-[0.22em] text-slate-700" data-testid="about-hero-eyebrow">
              <span className="w-6 h-[3px] rounded-full bg-[#F4C244]" aria-hidden="true" /> About us
            </span>
            <h1 className="m-0 mt-4 text-4xl sm:text-5xl lg:text-[3.4rem] font-extrabold tracking-tight leading-[1.06] text-slate-900" data-testid="landing-h1">
              {lp.h1 || lp.title}
            </h1>
            {aHero.subtitle && (() => {
              const words = String(aHero.subtitle).trim().split(/\s+/).filter(Boolean);
              const head = words.slice(0, -2).join(" ");
              const tail = words.slice(-2).join(" ");
              return (
                <p className="m-0 mt-3 text-3xl sm:text-4xl lg:text-[2.6rem] font-extrabold tracking-tight leading-[1.14] text-slate-900" data-testid="about-hero-subtitle">
                  {head && <span>{head} </span>}
                  <span className="bg-[#F4C244] px-1.5 rounded-sm box-decoration-clone">{tail}</span>
                </p>
              );
            })()}
            {aHero.intro && (
              <p className="m-0 mt-5 text-[15px] sm:text-base leading-relaxed text-slate-600 max-w-xl" data-testid="about-hero-intro">{aHero.intro}</p>
            )}
            <p className="m-0 mt-9 inline-block -rotate-2 text-3xl text-slate-800" style={{ fontFamily: "'Caveat', cursive" }} data-testid="about-hero-script">
              Skills for a Brighter Tomorrow.
            </p>
          </div>
          <div className="relative min-w-0">
            {aHero.image ? (
              <div className="relative">
                <img src={aHero.image} alt={aHero.image_alt || "Training Basket classroom"} loading="eager"
                  className="w-full rounded-3xl object-cover aspect-[4/3] shadow-[0_24px_60px_-20px_rgba(15,23,42,0.35)]" data-testid="about-hero-image" />
                {(aHero.icons || []).filter((i) => i && i.label).length > 0 && (
                  <div className="absolute top-5 left-5 sm:top-7 sm:left-7 -rotate-6 flex flex-col gap-1" data-testid="about-hero-icons">
                    {(aHero.icons || []).filter((i) => i && i.label).map((ic, i) => (
                      <span key={i} className="text-2xl sm:text-3xl font-bold uppercase leading-none text-white drop-shadow-[0_2px_6px_rgba(0,0,0,0.65)]"
                        style={{ fontFamily: "'Caveat', cursive" }} data-testid={`about-hero-icon-${i}`}>{ic.label}</span>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="w-full rounded-3xl bg-[#0A0F2B] aspect-[4/3]" aria-hidden="true" />
            )}
            <span className="hidden sm:flex flex-col items-end gap-0 absolute -top-6 right-2 rotate-3 text-[11px] font-bold uppercase tracking-[0.25em] text-slate-400 leading-relaxed text-right" data-testid="about-hero-vertical" aria-hidden="true">
              <span>Better</span><span>Skills</span><span>Brighter</span><span>Futures</span>
            </span>
          </div>
        </div>
      </section>
    ),

    about_intro: vis.about_intro && (
      <section key="about_intro" data-testid="about-intro" className={`grid gap-10 lg:gap-14 items-start ${aIntro.image ? "lg:grid-cols-2" : ""}`}>
        <div className={`min-w-0 ${aIntro.image ? "lg:order-2" : "max-w-3xl"}`}>
          <span className="inline-flex flex-col text-[11px] font-extrabold uppercase tracking-[0.22em] text-slate-700" data-testid="about-intro-eyebrow">
            Why we exist
            <span className="block w-10 h-[3px] rounded-full bg-[#F4C244] mt-2" aria-hidden="true" />
          </span>
          <h2 className="m-0 mt-4 text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">{aIntro.title || "About us"}</h2>
          {(aIntro.paragraphs || []).filter((p) => String(p).trim()).map((p, i) => (
            <p key={i} className="m-0 mt-4 text-[15px] leading-relaxed text-slate-600" data-testid={`about-intro-p-${i}`}>{p}</p>
          ))}
        </div>
        {aIntro.image && (
          <div className="relative min-w-0 lg:order-1">
            <img src={aIntro.image} alt={aIntro.image_alt || "Training Basket"} loading="lazy"
              className="w-full rounded-3xl object-cover aspect-[4/3] shadow-[0_24px_60px_-20px_rgba(15,23,42,0.3)]" data-testid="about-intro-image" />
            <div className="absolute top-5 left-5 -rotate-6 flex flex-col gap-1" aria-hidden="true" data-testid="about-intro-script">
              {["People", "Skills", "Opportunities"].map((w) => (
                <span key={w} className="text-2xl sm:text-3xl font-bold uppercase leading-none text-white drop-shadow-[0_2px_6px_rgba(0,0,0,0.65)]"
                  style={{ fontFamily: "'Caveat', cursive" }}>{w}</span>
              ))}
            </div>
          </div>
        )}
      </section>
    ),

    stats_band: vis.stats_band && (
      <section key="stats_band" data-testid="about-stats" className="rounded-3xl bg-[#0A0F2B] text-white px-7 sm:px-10 py-8 sm:py-10">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-y-6 sm:gap-y-0 sm:divide-x sm:divide-white/10">
          {aStats.map((s, i) => (
            <div key={i} className={`flex items-center gap-4 ${i > 0 ? "sm:pl-8" : ""}`} data-testid={`about-stat-${i}`}>
              <span className="w-12 h-12 rounded-2xl bg-amber-400/15 border border-amber-400/30 flex items-center justify-center shrink-0"><TrendingUp className="w-5 h-5 text-amber-400" /></span>
              <span className="flex flex-col min-w-0">
                <span className="text-2xl sm:text-3xl font-extrabold tracking-tight text-amber-400">{s.value}</span>
                <span className="text-xs font-bold uppercase tracking-wider text-slate-300">{s.label}</span>
              </span>
            </div>
          ))}
        </div>
      </section>
    ),

    team_grid: vis.team_grid && (
      <section key="team_grid" data-testid="about-team">
        <h2 className="m-0 text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900">Our Core Team</h2>
        <span className="block w-10 h-1 bg-amber-400 rounded mt-3" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-7">
          {aTeam.map((m, i) => (
            <article key={i} className="bg-white border border-slate-200/80 rounded-2xl p-6 shadow-[0_4px_20px_-4px_rgba(15,23,42,0.06)]" data-testid={`about-team-${i}`}>
              <div className="flex items-start gap-4">
                {m.photo ? (
                  <img src={m.photo} alt={m.name} loading="lazy" className="w-20 h-20 rounded-xl object-cover object-top bg-slate-100 shrink-0" />
                ) : (
                  <span className="w-20 h-20 rounded-xl bg-amber-50 border border-amber-200 flex items-center justify-center text-xl font-extrabold text-amber-700 shrink-0">{m.name.split(" ").map((w) => w[0]).slice(0, 2).join("")}</span>
                )}
                <span className="min-w-0">
                  <h3 className="m-0 text-lg font-extrabold text-slate-900">{m.name}</h3>
                  <p className="m-0 mt-0.5 text-xs font-bold text-amber-700">{m.role}</p>
                  {m.linkedin && (
                    <a href={m.linkedin} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 mt-2 text-xs font-semibold text-sky-700 hover:underline" data-testid={`about-team-linkedin-${i}`}>
                      <Linkedin className="w-3.5 h-3.5" /> LinkedIn
                    </a>
                  )}
                </span>
              </div>
              {m.bio && <p className="m-0 mt-4 text-sm leading-relaxed text-slate-600">{m.bio}</p>}
            </article>
          ))}
        </div>
      </section>
    ),

    partner_logos: vis.partner_logos && (
      <section key="partner_logos" data-testid="about-partners">
        <h2 className="m-0 text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900">Our Partners</h2>
        <span className="block w-10 h-1 bg-amber-400 rounded mt-3" />
        <div className="flex flex-wrap items-center gap-x-10 gap-y-5 mt-7" data-testid="about-partners-strip">
          {aPartners.map((p, i) => <PartnerLogo key={i} p={p} i={i} />)}
        </div>
      </section>
    ),

    media_cards: vis.media_cards && (
      <section key="media_cards" data-testid="about-media">
        <h2 className="m-0 text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900">Media</h2>
        <span className="block w-10 h-1 bg-amber-400 rounded mt-3" />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mt-7">
          {aMedia.map((m, i) => (
            <article key={i} className="bg-white border border-slate-200/80 rounded-2xl overflow-hidden shadow-[0_4px_20px_-4px_rgba(15,23,42,0.06)]" data-testid={`about-media-${i}`}>
              {m.image && <img src={m.image} alt={m.title} loading="lazy" className="w-full h-44 object-cover" />}
              <div className="p-6">
                <h3 className="m-0 text-base font-extrabold text-slate-900">{m.title}</h3>
                {m.text && <p className="m-0 mt-2 text-sm leading-relaxed text-slate-600">{m.text}</p>}
                {m.link_url && (
                  <a href={m.link_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 mt-3 text-sm font-bold text-amber-700 hover:underline" data-testid={`about-media-link-${i}`}>
                    {m.link_label || "Read Full Article"} <ArrowRight className="w-4 h-4" />
                  </a>
                )}
              </div>
            </article>
          ))}
        </div>
      </section>
    ),

    mission_vision: vis.mission_vision && (
      <section key="mission_vision" data-testid="about-mission-vision">
        <h2 className="m-0 text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900">{aMV.title || "Our Mission & Vision"}</h2>
        <span className="block w-10 h-1 bg-amber-400 rounded mt-3" />
        {aMV.intro && <p className="m-0 mt-4 text-[15px] leading-relaxed text-slate-600 max-w-3xl">{aMV.intro}</p>}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-7">
          <div className="bg-white border border-slate-200/80 rounded-2xl p-6" data-testid="about-mission-card">
            <span className="w-11 h-11 rounded-xl bg-amber-50 border border-amber-200 flex items-center justify-center"><Target className="w-5 h-5 text-amber-600" /></span>
            <h3 className="m-0 mt-4 text-base font-extrabold text-slate-900">{aMV.mission_title || "Our Mission"}</h3>
            {aMV.mission_text && <p className="m-0 mt-2 text-sm leading-relaxed text-slate-600">{aMV.mission_text}</p>}
          </div>
          <div className="bg-white border border-slate-200/80 rounded-2xl p-6" data-testid="about-vision-card">
            <span className="w-11 h-11 rounded-xl bg-amber-50 border border-amber-200 flex items-center justify-center"><Eye className="w-5 h-5 text-amber-600" /></span>
            <h3 className="m-0 mt-4 text-base font-extrabold text-slate-900">{aMV.vision_title || "Our Vision"}</h3>
            {aMV.vision_text && <p className="m-0 mt-2 text-sm leading-relaxed text-slate-600">{aMV.vision_text}</p>}
          </div>
          {aMV.image && <img src={aMV.image} alt={aMV.image_alt || "Training Basket"} loading="lazy" className="w-full h-full min-h-48 rounded-2xl object-cover" data-testid="about-mv-image" />}
        </div>
      </section>
    ),

    journey_timeline: vis.journey_timeline && (
      <section key="journey_timeline" data-testid="about-journey">
        <h2 className="m-0 text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900">Our Journey So Far</h2>
        <span className="block w-10 h-1 bg-amber-400 rounded mt-3" />
        <ol className="m-0 mt-8 p-0 list-none grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-8">
          {aJourney.map((j, i) => (
            <li key={i} className="relative border-t-2 border-slate-200 pt-4" data-testid={`about-journey-${i}`}>
              <span className="absolute -top-[7px] left-0 w-3 h-3 rounded-full bg-amber-400 border-2 border-white shadow" />
              <span className="text-sm font-extrabold text-amber-700">{j.year}</span>
              {j.title && <h3 className="m-0 mt-1 text-sm font-extrabold text-slate-900">{j.title}</h3>}
              {j.text && <p className="m-0 mt-1.5 text-[13px] leading-relaxed text-slate-500">{j.text}</p>}
            </li>
          ))}
        </ol>
      </section>
    ),

    contact_block: vis.contact_block && (
      <section key="contact_block" data-testid="about-contact" className="rounded-3xl bg-[#0A0F2B] text-white overflow-hidden">
        <div className="grid lg:grid-cols-2 gap-8 p-7 sm:p-10 items-start">
          <div className="min-w-0">
            <h2 className="m-0 text-2xl sm:text-3xl font-extrabold tracking-tight">{aContact.title || "Talk to our career advisors now"}</h2>
            <span className="block w-10 h-1 bg-amber-400 rounded mt-3" />
            <div className="flex flex-col gap-3 mt-6">
              {aContact.phone && (
                <a href={`tel:+91${aContact.phone.replace(/\D/g, "").slice(-10)}`} className="inline-flex items-center gap-3 text-lg font-bold text-white hover:text-amber-300 transition-colors" data-testid="about-contact-phone">
                  <span className="w-10 h-10 rounded-full bg-amber-400 text-[#0A0F2B] flex items-center justify-center shrink-0"><Phone className="w-4.5 h-4.5" /></span>{aContact.phone}
                </a>
              )}
              {aContact.whatsapp && (
                <a href={`https://wa.me/91${aContact.whatsapp.replace(/\D/g, "").slice(-10)}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-3 text-lg font-bold text-white hover:text-amber-300 transition-colors" data-testid="about-contact-whatsapp">
                  <span className="w-10 h-10 rounded-full bg-emerald-400 text-[#0A0F2B] flex items-center justify-center shrink-0"><MessageCircle className="w-4.5 h-4.5" /></span>{aContact.whatsapp}
                </a>
              )}
            </div>
            {aContact.note && <p className="m-0 mt-5 text-sm text-slate-300">{aContact.note}</p>}
          </div>
          <AboutContactForm lp={lp} subtitle={aContact.subtitle} />
        </div>
      </section>
    ),

    visit_maps: vis.visit_maps && (
      <section key="visit_maps" data-testid="about-maps">
        <h2 className="m-0 text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900">Map your Visit to Us</h2>
        <span className="block w-10 h-1 bg-amber-400 rounded mt-3" />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-7">
          {aMaps.map((m, i) => (
            <div key={i} className="bg-white border border-slate-200/80 rounded-2xl overflow-hidden" data-testid={`about-map-${i}`}>
              <div className="px-5 pt-5 pb-3">
                <h3 className="m-0 text-base font-extrabold text-slate-900 flex items-center gap-2"><MapPin className="w-4 h-4 text-amber-600" />{m.label}</h3>
                {m.address && <p className="m-0 mt-1 text-sm text-slate-500">{m.address}</p>}
              </div>
              {m.address && (
                <iframe title={m.label || `Map ${i + 1}`} src={`https://www.google.com/maps?q=${encodeURIComponent(m.address)}&output=embed`} loading="lazy" className="w-full h-64 border-0" />
              )}
            </div>
          ))}
        </div>
      </section>
    ),

    venue_form: vis.venue_form && (
      <section key="venue_form" data-testid="venue-booking" className="max-w-4xl mx-auto">
        <VenueBookingForm path={lp.url_path} />
      </section>
    ),

    overview: vis.overview && (
      <section id="overview" key="overview" className={overviewV2 ? "max-w-none" : "max-w-3xl"}>
        <div className={overviewV2 ? "min-w-0" : "bg-white rounded-2xl border border-slate-200/80 shadow-sm p-7 sm:p-9 min-w-0"}>
          <span className="text-xs font-semibold uppercase tracking-widest text-amber-700">Overview</span>
          <div className="flex items-start justify-between gap-6">
            <div className="min-w-0">
              {overviewV2 && ovTitle && <h2 className="m-0 mt-1 text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900" data-testid="landing-overview-title">{ovTitle}</h2>}
              {overviewV2 && ovMeta && (
                <span className="mt-3 inline-flex w-fit items-center gap-2 px-3.5 py-1.5 rounded-full bg-amber-50 border border-amber-200/70 text-[13px] font-bold text-amber-900" data-testid="landing-overview-meta">
                  <Clock className="w-3.5 h-3.5" /> {ovMeta}
                </span>
              )}
            </div>
            {overviewV2 && ovBadge && (
              <img src={ovBadge} alt="Certification badge" loading="lazy" className="w-20 sm:w-24 h-auto object-contain shrink-0 mt-1" data-testid="landing-overview-badge" />
            )}
          </div>
          <div className="mt-2">
            {contactSplit ? (
              <>
                <div className={overviewV2 ? "lp-body lp-body-v2" : "lp-body"} data-testid="landing-body" dangerouslySetInnerHTML={{ __html: bodyParts[0] }} />
                <ContactBlockForm lp={lp} />
                <div className={overviewV2 ? "lp-body lp-body-v2" : "lp-body"} data-testid="landing-body-after-contact" dangerouslySetInnerHTML={{ __html: bodyParts[1] }} />
              </>
            ) : (
              <div className={overviewV2 ? "lp-body lp-body-v2" : "lp-body"} data-testid="landing-body" dangerouslySetInnerHTML={{ __html: bodyParts[0] }} />
            )}
          </div>
          {overviewV2 && (ovCta || ovEnquire) && (
            <div className="mt-5 flex flex-wrap items-center gap-4">
              {ovCta && (
                <a href="#enquire" data-testid="landing-overview-cta"
                  className="inline-flex items-center gap-2 min-h-[50px] px-7 bg-[#F4C244] hover:bg-[#ecb733] active:scale-[.98] rounded-full font-extrabold text-[15px] text-slate-900 transition-all">
                  {ovCta} <ArrowRight className="w-[18px] h-[18px]" />
                </a>
              )}
              {ovEnquire && (
                <a href="#enquire" data-testid="landing-overview-enquire"
                  className="text-[15px] font-extrabold text-[#0A0F2B] underline decoration-[#F4C244] decoration-2 underline-offset-4 hover:decoration-4 transition-all">
                  {ovEnquire}
                </a>
              )}
            </div>
          )}
        </div>
      </section>
    ),

    roadmap: vis.roadmap && (
      <section key="roadmap" data-testid="landing-roadmap" className="rounded-3xl bg-[#0A0F2B] text-white relative overflow-hidden" aria-labelledby="lp-roadmap-h">
        <div className="absolute -top-24 -right-24 w-96 h-96 bg-[#F4C244]/10 rounded-full blur-3xl pointer-events-none" aria-hidden="true" />
        <div className="absolute -bottom-28 -left-20 w-80 h-80 bg-[#F4C244]/5 rounded-full blur-3xl pointer-events-none" aria-hidden="true" />
        <div className="relative p-8 sm:p-12">
          <span className="inline-flex items-center gap-2.5 text-[11px] font-extrabold uppercase tracking-[0.22em] text-[#F4C244]">
            <span className="w-6 h-[3px] rounded-full bg-[#F4C244]" aria-hidden="true" /> Program curriculum
          </span>
          <h2 id="lp-roadmap-h" className="m-0 mt-2 text-3xl sm:text-4xl font-extrabold tracking-tight leading-[1.1]">Your learning roadmap</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-8" data-testid="landing-roadmap-grid">
            {roadmapData.modules.map((md, i) => (
              <div key={i} className="flex flex-col gap-3 rounded-2xl bg-white/5 border border-white/10 p-5 hover:border-[#F4C244]/40 hover:bg-[#F4C244]/5 transition-all" data-testid={`landing-roadmap-module-${i}`}>
                <span className="inline-flex w-fit items-center gap-1.5 px-3 py-1 rounded-full bg-[#F4C244]/15 border border-[#F4C244]/35 text-[11px] font-extrabold text-[#F4C244]">
                  <Clock className="w-3 h-3" /> {md.weeks}
                </span>
                <h3 className="m-0 text-base font-extrabold text-white leading-snug">{`Module ${md.num} ${md.name}`}</h3>
                <div className="flex flex-wrap gap-1.5">
                  {md.topics.map((t, ti) => (
                    <span key={ti} className="px-2 py-0.5 rounded-md bg-white/5 border border-white/10 text-[11px] font-medium text-slate-300">{t}</span>
                  ))}
                </div>
                <a href="#enquire" className="mt-auto inline-flex items-center gap-1 text-[13px] font-bold text-[#F4C244] hover:gap-2 transition-all" data-testid={`landing-roadmap-explore-${i}`}>
                  Explore Curriculum <ArrowRight className="w-3.5 h-3.5" />
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>
    ),

    spec_tracks: vis.spec_tracks && (
      <section key="spec_tracks" data-testid="landing-spec-tracks" aria-labelledby="lp-spec-tracks-h">
        <span className="text-xs font-semibold uppercase tracking-widest text-amber-700">Go deeper</span>
        <h2 id="lp-spec-tracks-h" className="m-0 mt-1 text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">Specialization & career tracks</h2>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-8">
          {specTracksData.spec && (
            <div className="bg-white border border-slate-200/80 rounded-2xl p-6 sm:p-7 shadow-[0_4px_20px_-4px_rgba(15,23,42,0.06)]" data-testid="landing-spec-card">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <h3 className="m-0 text-xl font-extrabold text-slate-900">{specTracksData.spec.title}</h3>
                {specTracksData.spec.duration && (
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-50 border border-amber-200/70 text-[11px] font-extrabold text-amber-900">
                    <Clock className="w-3 h-3" /> {specTracksData.spec.duration}
                  </span>
                )}
              </div>
              {specTracksData.spec.labels.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-4">
                  {specTracksData.spec.labels.map((l, i) => (
                    <span key={i} className="px-3 py-1.5 rounded-full bg-[#0A0F2B] text-white text-xs font-bold">{l}</span>
                  ))}
                </div>
              )}
              {specTracksData.spec.topics.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-3">
                  {specTracksData.spec.topics.map((t, i) => (
                    <span key={i} className="px-2.5 py-1 rounded-md bg-slate-100 text-slate-600 text-[11px] font-semibold">{t}</span>
                  ))}
                </div>
              )}
              {specTracksData.spec.outcome && <p className="m-0 mt-4 text-sm leading-relaxed text-slate-500 border-t border-slate-100 pt-4">{specTracksData.spec.outcome}</p>}
            </div>
          )}
          {specTracksData.career && (
            <div className="bg-white border border-slate-200/80 rounded-2xl p-6 sm:p-7 shadow-[0_4px_20px_-4px_rgba(15,23,42,0.06)]" data-testid="landing-career-card">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <h3 className="m-0 text-xl font-extrabold text-slate-900">{specTracksData.career.title}</h3>
                {specTracksData.career.duration && (
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-50 border border-amber-200/70 text-[11px] font-extrabold text-amber-900">
                    <Clock className="w-3 h-3" /> {specTracksData.career.duration}
                  </span>
                )}
              </div>
              {specTracksData.career.modules.length > 0 && (
                <div className="mt-4 border-t border-slate-100">
                  {specTracksData.career.modules.map((m, i) => (
                    <div key={i} className="flex items-center justify-between gap-4 py-2.5 border-b border-slate-100" data-testid={`landing-career-module-${i}`}>
                      <span className="text-[15px] font-bold text-slate-900">{`Module ${m.num} ${m.name}`}</span>
                      {m.dur && <span className="text-xs font-semibold text-slate-500 shrink-0">{m.dur}</span>}
                    </div>
                  ))}
                </div>
              )}
              {specTracksData.career.topics.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-4">
                  {specTracksData.career.topics.map((t, i) => (
                    <span key={i} className="px-2.5 py-1 rounded-md bg-slate-100 text-slate-600 text-[11px] font-semibold">{t}</span>
                  ))}
                </div>
              )}
              {specTracksData.career.explore && (
                <a href="#enquire" className="mt-4 inline-flex items-center gap-1 text-[13px] font-bold text-amber-700 hover:gap-2 transition-all" data-testid="landing-career-explore">
                  {specTracksData.career.explore} <ArrowRight className="w-3.5 h-3.5" />
                </a>
              )}
            </div>
          )}
        </div>
      </section>
    ),

    tracks: vis.tracks && (
      <section key="tracks" data-testid="landing-tracks" aria-labelledby="lp-tracks-h">
        <span className="text-xs font-semibold uppercase tracking-widest text-amber-700">Program tracks</span>
        <h2 id="lp-tracks-h" className="m-0 mt-1 text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">Pick your 4/6-week track</h2>
        {trackData.branchLine && <p className="m-0 mt-2 text-sm font-medium text-slate-500" data-testid="landing-tracks-branches">{trackData.branchLine}</p>}
        <div className="mt-6 inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white p-1 shadow-sm" role="tablist" aria-label="Filter tracks by branch" data-testid="landing-tracks-filter">
          {[["all", "All tracks"], ["csit", "CS/IT"], ["bbamba", "BBA/MBA"]].map(([k, label]) => (
            <button key={k} type="button" role="tab" aria-selected={trackBranch === k} onClick={() => setTrackBranch(k)}
              data-testid={`landing-track-pill-${k}`}
              className={`min-h-[40px] px-5 rounded-full text-sm font-extrabold transition-all ${trackBranch === k ? "bg-[#0A0F2B] text-white shadow" : "text-slate-600 hover:text-slate-900 hover:bg-slate-100"}`}>
              {label}
            </button>
          ))}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-8" data-testid="landing-tracks-grid">
          {trackData.cards.map((cd, i) => {
            const branchKey = cd.branch === "BBA/MBA" ? "bbamba" : "csit";
            const hiddenCard = trackBranch !== "all" && branchKey !== trackBranch;
            return (
              <article key={i} data-testid={`landing-track-card-${i}`}
                className={`group bg-white border border-slate-200/80 rounded-2xl overflow-hidden shadow-[0_4px_20px_-4px_rgba(15,23,42,0.06)] hover:-translate-y-1 hover:shadow-[0_16px_36px_-8px_rgba(15,23,42,0.14)] hover:border-[#F4C244]/70 transition-all duration-200 ${hiddenCard ? "hidden" : ""}`}>
                <div className="h-36 sm:h-40 bg-slate-50 overflow-hidden border-b border-slate-100">
                  <img src={cd.src} alt={cd.name} loading="lazy" className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-300" />
                </div>
                <div className="p-5 flex flex-col gap-2.5">
                  <div className="flex flex-wrap gap-1.5">
                    <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-bold bg-amber-50 text-amber-900 border border-amber-200/70">{cd.duration}</span>
                    <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-bold bg-slate-100 text-slate-600">{cd.language}</span>
                  </div>
                  <h3 className="m-0 text-lg font-extrabold text-slate-900 leading-snug">{cd.name}</h3>
                  <div className="mt-auto flex items-center justify-between gap-3 pt-3 border-t border-slate-100">
                    <span className="inline-flex items-center gap-1.5 text-sm font-bold text-slate-700">
                      <Star className="w-4 h-4 text-[#F4C244] fill-[#F4C244]" /> {cd.rating} <span className="font-medium text-slate-500">{cd.learners} learners</span>
                    </span>
                    <a href="#enquire" className="inline-flex items-center gap-1 text-sm font-extrabold text-[#0A0F2B] hover:gap-2 transition-all" data-testid={`landing-track-more-${i}`}>
                      Know More <ArrowRight className="w-4 h-4" />
                    </a>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      </section>
    ),

    courses: vis.courses && (
      <section key="courses" data-testid="landing-courses" aria-labelledby="lp-courses-h">
        <span className="text-xs font-semibold uppercase tracking-widest text-amber-700">Course showcase</span>
        <h2 id="lp-courses-h" className="m-0 mt-1 text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">Browse our {courseData.brand} Courses</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-8" data-testid="landing-courses-grid">
          {courseData.cards.map((cd, i) => (
            <article key={i} data-testid={`landing-course-card-${i}`}
              className="group bg-white border border-slate-200/80 rounded-2xl overflow-hidden shadow-[0_4px_20px_-4px_rgba(15,23,42,0.06)] hover:-translate-y-1 hover:shadow-[0_16px_36px_-8px_rgba(15,23,42,0.14)] hover:border-[#F4C244]/70 transition-all duration-200">
              <div className="h-44 bg-slate-50/60 border-b border-slate-100 flex items-center justify-center p-6 overflow-hidden">
                <img src={cd.src} alt={cd.name} loading="lazy" className="max-h-full w-auto max-w-full object-contain group-hover:scale-[1.04] transition-transform duration-300" />
              </div>
              <div className="p-5 flex flex-col gap-2.5">
                <div className="flex flex-wrap gap-1.5">
                  <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-bold bg-amber-50 text-amber-900 border border-amber-200/70">{cd.duration}</span>
                  <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-bold bg-slate-100 text-slate-600">{cd.language}</span>
                </div>
                <h3 className="m-0 text-lg font-extrabold text-slate-900 leading-snug">{cd.name}</h3>
                <div className="mt-auto flex items-center justify-between gap-3 pt-3 border-t border-slate-100">
                  <span className="inline-flex items-center gap-1.5 text-sm font-bold text-slate-700">
                    <Star className="w-4 h-4 text-[#F4C244] fill-[#F4C244]" /> {cd.rating} <span className="font-medium text-slate-500">{cd.learners} learners</span>
                  </span>
                  <a href="#enquire" className="inline-flex items-center gap-1 text-sm font-extrabold text-[#0A0F2B] hover:gap-2 transition-all" data-testid={`landing-course-more-${i}`}>
                    Know More <ArrowRight className="w-4 h-4" />
                  </a>
                </div>
              </div>
            </article>
          ))}
        </div>
      </section>
    ),

    why_enroll: vis.why_enroll && (
      <section key="why_enroll" data-testid="landing-why-enroll" aria-labelledby="lp-why-enroll-h">
        <span className="text-xs font-semibold uppercase tracking-widest text-amber-700">Career outcomes</span>
        <h2 id="lp-why-enroll-h" className="m-0 mt-1 text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">{whyEnrollData.title}</h2>
        {whyEnrollData.items.some((it) => it.big) ? (
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-6">
            {whyEnrollData.items.map((it, i) => (
              <figure key={i} className="m-0 bg-white border border-slate-200/80 rounded-2xl overflow-hidden shadow-[0_4px_20px_-4px_rgba(15,23,42,0.06)] hover:-translate-y-1 hover:shadow-[0_12px_30px_-6px_rgba(15,23,42,0.12)] transition-all duration-200" data-testid={`landing-why-enroll-${i}`}>
                <div className="bg-slate-50/80 px-6 py-5 flex items-center justify-center border-b border-slate-100">
                  <img src={it.src} alt={it.alt} loading="lazy" className="h-24 sm:h-28 w-auto object-contain" />
                </div>
                <figcaption className="p-5 text-[15px] leading-relaxed text-slate-600">{it.text}</figcaption>
              </figure>
            ))}
          </div>
        ) : (
        <div className="mt-8 border-t border-slate-200">
          {whyEnrollData.items.map((it, i) => (
            <div key={i} className="flex flex-col sm:flex-row items-start sm:items-center gap-5 sm:gap-8 py-6 border-b border-slate-200" data-testid={`landing-why-enroll-${i}`}>
              <img src={it.src} alt={it.alt} loading="lazy" className="w-20 h-20 sm:w-24 sm:h-24 rounded-full object-cover shrink-0 shadow-md" />
              <p className="m-0 text-[15px] sm:text-base leading-relaxed text-slate-600 max-w-3xl">{it.text}</p>
            </div>
          ))}
        </div>
        )}
        {whyEnrollData.cta && (
          <div className="mt-8">
            <a href="#enquire" data-testid="landing-why-enroll-cta"
              className="inline-flex items-center gap-2 min-h-[54px] px-8 bg-[#F4C244] hover:bg-[#ecb733] active:scale-[.98] rounded-full font-extrabold text-base text-slate-900 shadow-[0_10px_24px_rgba(217,161,27,.35)] transition-all">
              {whyEnrollData.cta} <ArrowRight className="w-[18px] h-[18px]" />
            </a>
          </div>
        )}
      </section>
    ),

    journey: vis.journey && (
      <section id="journey" key="journey" aria-labelledby="lp-journey-h" data-testid="landing-journey">
        {journeyData.stats.some((s) => s.desc) ? (
          <div className="py-4 sm:py-8" data-testid="landing-journey-path">
            <h2 id="lp-journey-h" className="m-0 text-center text-2xl sm:text-3xl font-extrabold tracking-tight text-[#2563EB]">{journeyData.title}</h2>
            <div className="relative mt-8 sm:mt-12">
              <svg className="hidden lg:block absolute inset-x-0 top-1/2 -translate-y-1/2 w-full h-44 pointer-events-none" viewBox="0 0 1200 180" fill="none" preserveAspectRatio="none" aria-hidden="true">
                <path d="M0,30 C150,130 260,130 400,50 C540,-20 660,-20 800,60 C940,140 1060,140 1200,50" stroke="#1E3A8A" strokeWidth="2.5" strokeDasharray="7 9" strokeLinecap="round" />
              </svg>
              <div className="relative grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-12">
                {journeyData.stats.map((s, i) => (
                  <div key={i} className={`flex flex-col items-center text-center ${i % 2 === 1 ? "lg:mt-20" : ""}`} data-testid={`landing-journey-stat-${i}`}>
                    {s.src && (
                      <img src={s.src} alt={s.label} loading="lazy"
                        className="w-44 h-36 sm:w-52 sm:h-40 object-cover"
                        style={{ borderRadius: "58% 42% 55% 45% / 45% 52% 48% 55%" }} />
                    )}
                    <h3 className="m-0 mt-4 text-lg font-extrabold text-slate-900">{s.label}</h3>
                    <p className="m-0 mt-1.5 text-sm leading-relaxed text-slate-600 max-w-[250px]" dangerouslySetInnerHTML={{ __html: s.desc }} />
                  </div>
                ))}
              </div>
            </div>
          </div>
        ) : (
        <div className="border-y border-slate-200 py-8 sm:py-10">
          <span className="text-xs font-semibold uppercase tracking-widest text-amber-700">Track record</span>
          <h2 id="lp-journey-h" className="m-0 mt-1 text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900">{journeyData.title}</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-y-6 sm:gap-y-0 sm:divide-x sm:divide-slate-200 mt-6">
            {journeyData.stats.map((s, i) => (
              <div key={i} className={`flex items-center gap-4 ${i > 0 ? "sm:pl-8" : ""}`} data-testid={`landing-journey-stat-${i}`}>
                {s.src && (
                  <span className="shrink-0 inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-amber-50 border border-amber-200/70 p-2.5">
                    <img src={s.src} alt="" className="w-full h-full object-contain" loading="lazy" />
                  </span>
                )}
                <span className="flex flex-col min-w-0">
                  <span className="text-3xl font-extrabold tracking-tight text-slate-900">{formatStat(s.value)}</span>
                  <span className="text-sm font-medium text-slate-500">{s.label}</span>
                </span>
              </div>
            ))}
          </div>
        </div>
        )}
      </section>
    ),

    instructors: vis.instructors && (
      <section id="instructors" key="instructors" aria-labelledby="lp-instr-h" data-testid="landing-instructors">
        <span className="text-xs font-semibold uppercase tracking-widest text-amber-700">Faculty</span>
        <h2 id="lp-instr-h" className="m-0 mt-1 text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">{instructorsData.title}</h2>
        {instructorsData.intro && <p className="m-0 mt-2 text-[15px] leading-relaxed text-slate-600 max-w-2xl">{instructorsData.intro}</p>}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-7">
          {instructorsData.people.map((p, i) => {
            const exp = p.meta.match(/^([\d+]+\+?\s*years?(?:\s+of)?\s+experience)\s*[·\-–]?\s*(.*)$/i);
            return (
              <article key={i} className="bg-white border border-slate-200/80 rounded-2xl overflow-hidden shadow-[0_4px_20px_-4px_rgba(15,23,42,0.06)] hover:-translate-y-1 hover:border-amber-400/80 hover:shadow-[0_12px_30px_-6px_rgba(15,23,42,0.12)] transition-all duration-200" data-testid={`landing-instructor-${i}`}>
                <div className="aspect-square sm:aspect-[4/5] bg-slate-100 overflow-hidden">
                  <img src={p.src} alt={p.name} loading="lazy" className="w-full h-full object-cover object-top" />
                </div>
                <div className="p-6 flex flex-col gap-2">
                  <h3 className="m-0 text-lg font-extrabold text-slate-900">{p.name}</h3>
                  {exp ? (
                    <>
                      <span className="self-start inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-bold bg-amber-100 text-amber-900 border border-amber-300/60">{exp[1]}</span>
                      {exp[2] && <p className="m-0 text-sm text-slate-600">{exp[2]}</p>}
                    </>
                  ) : (
                    <p className="m-0 text-sm text-slate-600">{p.meta}</p>
                  )}
                </div>
              </article>
            );
          })}
        </div>
        {instructorsData.outro && <p className="m-0 mt-6 text-[15px] leading-relaxed text-slate-600 max-w-2xl">{instructorsData.outro}</p>}
      </section>
    ),

    alumni_logos: vis.alumni_logos && (
      <section id="alumni-logos" key="alumni_logos" aria-labelledby="lp-alumni-logos-h" data-testid="landing-alumni-logos">
        <span className="text-xs font-semibold uppercase tracking-widest text-amber-700">Where our alumni work</span>
        <h2 id="lp-alumni-logos-h" className="m-0 mt-1 text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">{alumniLogosData.title}</h2>
        {alumniLogosData.intro && <p className="m-0 mt-2 text-[15px] leading-relaxed text-slate-600 max-w-2xl">{alumniLogosData.intro}</p>}
        <LogoMarquee logos={alumniLogosData.logos} light label="Companies where alumni work" testidPrefix="landing-alumni-logo" />
        {alumniLogosData.outro && <p className="m-0 mt-5 text-[15px] leading-relaxed text-slate-600 max-w-2xl" data-testid="landing-alumni-outro">{alumniLogosData.outro}</p>}
        {alumniLogosData.cta && (
          <a href="#enquire" data-testid="landing-alumni-cta"
            className="mt-5 inline-flex items-center gap-2 min-h-[50px] px-7 bg-[#F4C244] hover:bg-[#ecb733] active:scale-[.98] rounded-full font-extrabold text-[15px] text-slate-900 transition-all">
            {alumniLogosData.cta} <ArrowRight className="w-[18px] h-[18px]" />
          </a>
        )}
      </section>
    ),

    certification: vis.certification && (
      <section key="certification" data-testid="landing-certification" className="rounded-3xl bg-[#0A0F2B] text-white relative overflow-hidden">
        <div className="absolute -top-24 -right-24 w-96 h-96 bg-[#F4C244]/10 rounded-full blur-3xl pointer-events-none" aria-hidden="true" />
        <div className="absolute -bottom-28 -left-20 w-80 h-80 bg-[#F4C244]/5 rounded-full blur-3xl pointer-events-none" aria-hidden="true" />
        <div className="relative p-8 sm:p-12">
          <div className={`grid ${certData.certImage ? "lg:grid-cols-2" : ""} gap-10 lg:gap-14 items-center`}>
            <div className="min-w-0 flex flex-col gap-5">
              <span className="inline-flex items-center gap-2.5 text-[11px] font-extrabold uppercase tracking-[0.22em] text-[#F4C244]">
                <span className="w-6 h-[3px] rounded-full bg-[#F4C244]" aria-hidden="true" /> Certification
              </span>
              <h2 className="m-0 text-3xl sm:text-4xl font-extrabold tracking-tight leading-[1.1]" data-testid="landing-cert-title">{certData.title}</h2>
              {certData.intro && <p className="m-0 text-[15px] sm:text-base leading-relaxed text-slate-300" data-testid="landing-cert-intro">{certData.intro}</p>}
              {(certData.extraParas || []).map((x, i) => (
                <p key={i} className="m-0 text-[15px] sm:text-base leading-relaxed text-slate-300" data-testid={`landing-cert-para-${i}`}>{x}</p>
              ))}
              {certData.perks.length > 0 && (
                <div className="flex flex-col gap-3.5 mt-1">
                  {certData.perks.map((p, i) => (
                    <div key={i} className="flex items-start gap-3" data-testid={`landing-cert-perk-${i}`}>
                      <span className="w-6 h-6 rounded-full bg-[#F4C244]/20 border border-[#F4C244]/40 flex items-center justify-center shrink-0 mt-0.5">
                        <Check className="w-3.5 h-3.5 text-[#F4C244]" />
                      </span>
                      <p className="m-0 text-[15px] leading-relaxed text-slate-200">{p}</p>
                    </div>
                  ))}
                </div>
              )}
              {certData.cta && (
                <div className="mt-2">
                  <a href="#enquire" data-testid="landing-cert-cta"
                    className="inline-flex items-center gap-2 min-h-[54px] px-8 bg-[#F4C244] hover:bg-[#ecb733] active:scale-[.98] rounded-full font-extrabold text-base text-slate-900 shadow-[0_10px_24px_rgba(244,194,68,.35)] transition-all">
                    {certData.cta} <ArrowRight className="w-[18px] h-[18px]" />
                  </a>
                </div>
              )}
            </div>
            {certData.certImage && (
              <div className="relative min-w-0">
                <div className="absolute -inset-2 rounded-3xl border-2 border-[#F4C244]/20 pointer-events-none" aria-hidden="true" />
                <img src={certData.certImage} alt="Course completion certificate" loading="lazy"
                  className="relative w-full rounded-2xl border border-[#F4C244]/30 object-cover shadow-[0_24px_60px_-20px_rgba(0,0,0,0.5)]" data-testid="landing-cert-image" />
              </div>
            )}
          </div>
          {(certData.stats || []).length > 0 && (
            <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="landing-cert-stats">
              {certData.stats.map((s, i) => (
                <div key={i} className="flex items-center gap-4 rounded-2xl bg-white/5 border border-white/10 px-5 py-4" data-testid={`landing-cert-stat-${i}`}>
                  {s.src && <img src={s.src} alt="" loading="lazy" className="w-10 h-10 object-contain shrink-0" />}
                  <span className="flex flex-col min-w-0">
                    <span className="text-2xl font-extrabold tracking-tight text-[#F4C244]">{/^\d+\+?$/.test(s.value) ? (s.value.endsWith("+") ? formatStat(s.value.slice(0, -1)) + "+" : formatStat(s.value)) : s.value}</span>
                    <span className="text-sm font-medium text-slate-300">{s.label}</span>
                  </span>
                </div>
              ))}
            </div>
          )}
          {certData.features.length > 0 && (
            <div className="mt-10 pt-8 border-t border-white/10">
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="landing-cert-features">
                {certData.features.map((f, i) => (
                  <span key={i} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white/5 border border-white/10 text-sm font-semibold text-slate-200 hover:border-[#F4C244]/40 hover:bg-[#F4C244]/10 transition-all" data-testid={`landing-cert-feature-${i}`}>
                    <Zap className="w-3.5 h-3.5 text-[#F4C244] shrink-0" /> {f}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>
    ),

    contact_strip: vis.contact_strip && (
      <section key="contact_strip" data-testid="landing-contact-strip" className="rounded-3xl bg-[#0A0F2B] text-white p-8 sm:p-10 flex flex-col sm:flex-row sm:items-center gap-6 sm:gap-10 relative overflow-hidden">
        <div className="absolute -bottom-20 -right-20 w-72 h-72 bg-[#F4C244]/10 rounded-full blur-3xl pointer-events-none" aria-hidden="true" />
        <div className="min-w-0 flex-1 relative">
          <span className="text-[11px] font-extrabold uppercase tracking-[0.22em] text-[#F4C244]">{contactData.title}</span>
          {contactData.intro && <p className="m-0 mt-2 text-lg font-bold text-white" data-testid="landing-contact-strip-intro">{contactData.intro}</p>}
          {contactData.note && <p className="m-0 mt-1 text-sm text-slate-300" data-testid="landing-contact-strip-note">{contactData.note}</p>}
        </div>
        {contactData.phone && (
          <a href={`tel:${contactData.phone}`} data-testid="landing-contact-strip-phone"
            className="relative inline-flex items-center gap-3 shrink-0 rounded-2xl border border-[#F4C244]/40 bg-[#F4C244]/10 px-6 py-4 hover:bg-[#F4C244]/20 transition-all">
            <Phone className="w-5 h-5 text-[#F4C244] shrink-0" />
            <span className="flex flex-col">
              <span className="text-[11px] font-bold uppercase tracking-wider text-slate-300">{contactData.phoneLabel}</span>
              <span className="text-lg font-extrabold text-white">{contactData.phoneDisplay || contactData.phone}</span>
            </span>
          </a>
        )}
      </section>
    ),

    highlights: vis.highlights && (
      <section id="highlights" aria-labelledby="lp-highlights-h" key="highlights" data-testid="landing-highlights">
        <span className="text-xs font-semibold uppercase tracking-widest text-amber-700">Why this course</span>
        <h2 id="lp-highlights-h" className="m-0 mt-1 text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">Course highlights</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-7">
          {highlights.map((h, i) => {
            const Icon = ICONS[h.icon] || Zap;
            const titleOnly = !h.text;
            return (
              <div key={i}
                className={`${i === 0 && !titleOnly ? "md:col-span-2 border-amber-300/70 bg-gradient-to-br from-amber-50 to-white" : "border-slate-200/80 bg-white"} border rounded-2xl p-7 flex ${titleOnly ? "flex-row items-center gap-4" : "flex-col gap-3"} shadow-[0_4px_20px_-4px_rgba(15,23,42,0.06)] hover:shadow-[0_12px_30px_-6px_rgba(15,23,42,0.12)] hover:-translate-y-1 hover:border-amber-400/80 transition-all duration-200`}
                data-testid={`landing-highlight-${i}`}>
                <span className="bg-amber-100 text-amber-800 p-3 rounded-xl inline-block self-start shrink-0">
                  <Icon className="w-6 h-6" />
                </span>
                <h3 className="m-0 text-xl font-bold tracking-tight text-slate-900">{h.title}</h3>
                {h.text && <p className="m-0 text-[15px] leading-relaxed text-slate-600">{h.text}</p>}
              </div>
            );
          })}
        </div>
      </section>
    ),

    curriculum: vis.curriculum && (
      <section id="curriculum" key="curriculum" className="grid lg:grid-cols-12 gap-8 items-start" data-testid="landing-curriculum">
        <div className="lg:col-span-4 lg:sticky lg:top-24 bg-slate-900 rounded-2xl p-7 text-white flex flex-col gap-4 min-w-0">
          <span className="text-xs font-semibold uppercase tracking-widest text-amber-400">Curriculum</span>
          <h2 className="m-0 text-2xl sm:text-3xl font-extrabold tracking-tight">{blobCurr?.title || "What you'll master"}</h2>
          {!blobCurr && (
            <p className="m-0 text-[15px] leading-relaxed text-slate-300">
              Structured around the certification exam areas, taught over {duration || "the course"} with labs after every module.
            </p>
          )}
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-slate-300">
            <span className="inline-flex items-center gap-1.5"><Layers className="w-4 h-4 text-amber-400" /> {modules.length} modules</span>
            {!blobCurr && <span className="inline-flex items-center gap-1.5"><FlaskConical className="w-4 h-4 text-amber-400" /> Lab after every module</span>}
          </div>
          {blobCurr?.note && <p className="m-0 text-xs text-slate-400">{blobCurr.note}</p>}
          {prog?.syllabus_pdf && (
            <a href={prog.syllabus_pdf} target="_blank" rel="noreferrer" data-testid="landing-curriculum-syllabus"
              className="inline-flex items-center justify-center gap-2 min-h-[48px] px-5 bg-[#F4C244] hover:bg-[#ecb733] active:scale-[.98] rounded-full font-extrabold text-[15px] text-slate-900 transition-all">
              <Download className="w-[18px] h-[18px]" /> Download full syllabus (PDF)
            </a>
          )}
        </div>
        <div className="lg:col-span-8 flex flex-col gap-3 lp-acc min-w-0">
          {modules.map((m, i) => (
            m.description ? (
              <details key={i} open={i === 0} className="bg-white border border-slate-200/80 rounded-2xl px-6 shadow-sm hover:border-amber-400/70 transition-colors" data-testid={`landing-module-${i}`}>
                <summary className="min-h-[64px] gap-4">
                  <span className="inline-flex items-center gap-4 min-w-0">
                    <span className="text-[11px] font-mono font-bold px-2.5 py-1 rounded-lg bg-slate-900 text-amber-400 shrink-0">{`MODULE ${String(i + 1).padStart(2, "0")}`}</span>
                    <span className="font-bold text-[16px] text-slate-900">{m.title}</span>
                  </span>
                  <ChevronDown className="lp-chev w-5 h-5 shrink-0 text-slate-400" />
                </summary>
                <p className="m-0 pb-5 text-[15px] leading-relaxed text-slate-600">{m.description}</p>
              </details>
            ) : (
              <div key={i} className="bg-white border border-slate-200/80 rounded-2xl px-6 shadow-sm hover:border-amber-400/70 transition-colors flex items-center min-h-[64px]" data-testid={`landing-module-${i}`}>
                <span className="inline-flex items-center gap-4 min-w-0">
                  <span className="text-[11px] font-mono font-bold px-2.5 py-1 rounded-lg bg-slate-900 text-amber-400 shrink-0">{`MODULE ${String(i + 1).padStart(2, "0")}`}</span>
                  <span className="font-bold text-[16px] text-slate-900">{m.title}</span>
                </span>
              </div>
            )
          ))}
        </div>
      </section>
    ),

    who: vis.who && (
      <section id="who" key="who" className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="landing-who">
        {whoFor.length > 0 && (
          <div className="bg-slate-50 border border-slate-200/80 rounded-2xl p-8 flex flex-col gap-4">
            <span className="bg-white border border-slate-200 p-3 rounded-xl self-start">
              <Target className="w-6 h-6 text-amber-700" />
            </span>
            <h2 className="m-0 text-2xl font-extrabold text-slate-900">Who this course is for</h2>
            <ul className="m-0 flex flex-col gap-2.5 text-[15px] text-slate-700">
              {whoFor.map((w, i) => (
                <li key={i} className="flex gap-2.5" data-testid={`landing-who-${i}`}>
                  <Check className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" /> {w}
                </li>
              ))}
            </ul>
          </div>
        )}
        {prereqs.length > 0 && (
          <div className="bg-amber-50/50 border border-amber-200/60 rounded-2xl p-8 flex flex-col gap-4">
            <span className="bg-white border border-amber-200 p-3 rounded-xl self-start">
              <Lightbulb className="w-6 h-6 text-amber-700" />
            </span>
            <h2 className="m-0 text-2xl font-extrabold text-slate-900">Prerequisites</h2>
            <ul className="m-0 flex flex-col gap-2.5 text-[15px] text-slate-700">
              {prereqs.map((p, i) => (
                <li key={i} className="flex gap-2.5" data-testid={`landing-prereq-${i}`}>
                  <Check className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" /> {p}
                </li>
              ))}
            </ul>
          </div>
        )}
      </section>
    ),

    placement: vis.placement && (
      <section id="placement" key="placement" className="rounded-3xl bg-[#0B1120] text-white p-8 sm:p-12 flex flex-col gap-9 relative overflow-hidden" data-testid="landing-placement">
        <div className="absolute -top-24 -right-24 w-96 h-96 bg-amber-400/15 rounded-full blur-3xl pointer-events-none" aria-hidden="true" />
        <div className="flex flex-wrap gap-6 items-end justify-between relative">
          <div className="max-w-xl flex flex-col gap-3 min-w-0">
            <span className="text-xs font-semibold uppercase tracking-widest text-amber-400">Placements</span>
            <h2 className="m-0 text-3xl sm:text-4xl font-extrabold tracking-tight">
              100% Placement Support <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-300 to-amber-500">with Job Basket</span>
            </h2>
            {prog?.placement_text && <p className="m-0 text-[16px] leading-relaxed text-slate-300">{prog.placement_text}</p>}
          </div>
          <a href="https://www.jobbasket.in" target="_blank" rel="noreferrer" data-testid="landing-placement-jobbasket"
            className="inline-flex items-center gap-2 min-h-[50px] px-7 bg-[#F4C244] hover:bg-[#ecb733] active:scale-[.98] rounded-full font-extrabold text-[15px] text-slate-900 transition-all">
            Explore placement records <ArrowRight className="w-[18px] h-[18px]" />
          </a>
        </div>
        <div className="flex flex-wrap gap-x-10 gap-y-4 relative">
          {[["100%", "Job assistance via Job Basket"], ["10,000+", "Learners trained"], ["4.5/5", "Google rating"]].map((s) => (
            <div key={s[1]} className="flex flex-col">
              <span className="text-3xl font-extrabold text-amber-400 tracking-tight">{s[0]}</span>
              <span className="text-sm text-slate-400 font-medium">{s[1]}</span>
            </div>
          ))}
        </div>
        {logos.length > 0 && (
          <div className="relative" data-testid="landing-logos">
            <span className="text-[12px] font-semibold uppercase tracking-widest text-slate-400">Our alumni work at · Hiring partners</span>
            <LogoMarquee logos={logos} label="Hiring partner logos" />
          </div>
        )}
      </section>
    ),

    stories: vis.stories && (
      <section id="stories" key="stories" data-testid="landing-testimonials">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <span className="text-xs font-semibold uppercase tracking-widest text-amber-700">Success stories</span>
            <h2 className="m-0 mt-1 text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">{blobStories?.title || "Alumni stories"}</h2>
          </div>
          <div className="flex items-center gap-1.5 text-sm font-semibold text-slate-600">
            <Star className="w-[18px] h-[18px] fill-amber-400 text-amber-400" /> 4.5/5 Google rating
          </div>
        </div>
        {blobStories?.intro && <p className="m-0 mt-3 text-[15px] leading-relaxed text-slate-600 max-w-2xl" data-testid="landing-stories-intro">{blobStories.intro}</p>}
        {alumniPhotos.length > 0 && (
          <div className="flex items-center gap-3 mt-6" data-testid="landing-alumni-photos">
            <div className="flex -space-x-3">
              {alumniPhotos.slice(0, 5).map((src, i) => (
                <img key={i} src={src} alt="Training Basket graduate" loading="lazy"
                  className="w-11 h-11 rounded-full object-cover border-[3px] border-white shadow-md" />
              ))}
            </div>
            <span className="text-sm font-medium text-slate-500">10,000+ satisfied learners</span>
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
          {stories.map((t, i) => (
            <figure key={i} className="m-0 bg-white border border-slate-200/80 rounded-2xl p-7 flex flex-col gap-4 shadow-[0_4px_20px_-4px_rgba(15,23,42,0.06)] hover:-translate-y-1 hover:border-amber-400/80 transition-all duration-200" data-testid={`landing-testimonial-${i}`}>
              <div className="flex gap-1" aria-hidden="true">
                {[0, 1, 2, 3, 4].map((s) => <Star key={s} className="w-4 h-4 fill-amber-400 text-amber-400" />)}
              </div>
              <blockquote className="m-0 text-base leading-relaxed text-slate-700">“{t.text}”</blockquote>
              {(t.name || t.role) && (
                <figcaption className="flex items-center gap-3 mt-auto">
                  {t.src ? (
                    <img src={t.src} alt={t.name || "Training Basket graduate"} loading="lazy" className="w-11 h-11 rounded-full object-cover border-2 border-amber-200 shrink-0" />
                  ) : (
                    <span className="w-11 h-11 rounded-full bg-slate-900 text-amber-400 font-extrabold inline-flex items-center justify-center text-sm shrink-0">
                      {(t.name || "TB").split(" ").map((x) => x[0]).join("").slice(0, 2).toUpperCase()}
                    </span>
                  )}
                  <span className="flex flex-col min-w-0">
                    <strong className="text-[15px] text-slate-900">{t.name}</strong>
                    <span className="text-[13px] text-slate-500">{t.role}</span>
                  </span>
                </figcaption>
              )}
            </figure>
          ))}
        </div>
        {blobStories?.outro && <p className="m-0 mt-6 text-[15px] leading-relaxed text-slate-600 max-w-3xl" data-testid="landing-stories-outro">{blobStories.outro}</p>}
        {blobStories?.cta && (
          <div className="mt-8">
            <a href="#enquire" data-testid="landing-stories-cta"
              className="inline-flex items-center gap-2 min-h-[54px] px-8 bg-[#F4C244] hover:bg-[#ecb733] active:scale-[.98] rounded-full font-extrabold text-base text-slate-900 shadow-[0_10px_24px_rgba(217,161,27,.35)] transition-all">
              {blobStories.cta} <ArrowRight className="w-[18px] h-[18px]" />
            </a>
          </div>
        )}
      </section>
    ),

    placed: vis.placed && (
      <section id="placed" key="placed" data-testid="landing-placed" aria-labelledby="lp-placed-h">
        <span className="text-xs font-semibold uppercase tracking-widest text-amber-700">Placements in action</span>
        <h2 id="lp-placed-h" className="m-0 mt-1 text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">{placedData.title}</h2>
        {placedData.intro && <p className="m-0 mt-2 text-[15px] leading-relaxed text-slate-600 max-w-2xl">{placedData.intro}</p>}
        <PlacedSlider students={placedData.students} />
      </section>
    ),

    centre: vis.centre && (
      <section id="centre" aria-labelledby="lp-centre-h" key="centre" className="grid lg:grid-cols-2 gap-6 items-stretch" data-testid="landing-centre">
        <div className="bg-white border border-slate-200/80 rounded-2xl p-8 flex flex-col gap-3.5 shadow-sm min-w-0">
          <span className="text-xs font-semibold uppercase tracking-widest text-amber-700">Campus</span>
          <h2 id="lp-centre-h" className="m-0 text-2xl font-extrabold text-slate-900">
            {cityRec.has_centre ? `Train at our ${cityRec.name} centre` : `${cityRec.name} — learn live online`}
          </h2>
          {cityRec.address && <p className="m-0 text-[15px] leading-relaxed text-slate-600 flex items-start gap-2"><MapPin className="w-[18px] h-[18px] mt-0.5 shrink-0 text-amber-700" />{cityRec.address}</p>}
          {(cityRec.phones || []).length > 0 && (
            <div className="flex flex-wrap gap-2">
              {cityRec.phones.map((p, i) => (
                <a key={p} href={`tel:${p.replace(/\s/g, "")}`}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-slate-900 text-white text-sm font-bold hover:bg-slate-800 transition-colors"
                  data-testid={`landing-centre-phone-${i}`}>
                  <Phone className="w-3.5 h-3.5" /> {p}
                </a>
              ))}
            </div>
          )}
          <p className="m-0 text-sm text-slate-500">
            {cityRec.has_centre
              ? "Prefer to learn from home? Every batch also runs live online."
              : cityRec.nearest_text || "Every batch runs live online — join from anywhere."}
          </p>
          {(lp.other_city_pages || []).length > 0 && (
            <p className="m-0 mt-1 text-sm text-slate-500">
              Also available near you:{" "}
              {lp.other_city_pages.map((o, i) => (
                <span key={o.url_path}>
                  {i > 0 && " · "}
                  <Link to={o.url_path} className="font-bold text-slate-900 hover:text-amber-700 transition-colors" data-testid={`landing-centre-other-${i}`}>
                    {`CCNA in ${o.city}`}
                  </Link>
                </span>
              ))}
            </p>
          )}
        </div>
        {cityRec.map_embed && (
          <div className="rounded-2xl overflow-hidden bg-slate-200 min-h-[260px] border border-slate-200" data-testid="landing-centre-map">
            <iframe title={`Map — ${cityRec.name}`} src={cityRec.map_embed} className="w-full h-full min-h-[260px] border-0" loading="lazy" />
          </div>
        )}
      </section>
    ),

    faqs: vis.faqs && (
      <section id="faq" key="faqs" className="max-w-3xl lp-acc" data-testid="landing-faqs">
        <span className="text-xs font-semibold uppercase tracking-widest text-amber-700">Questions</span>
        <h2 className="m-0 mt-1 text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">{blobFaqs?.title || "Frequently asked questions"}</h2>
        <div className="flex flex-col gap-3 mt-7">
          {faqs.map((f, i) => (
            <details key={i} open={i === 0} className="bg-white border border-slate-200/80 rounded-2xl px-6 shadow-sm hover:border-amber-400/70 transition-colors" data-testid={`landing-faq-${i}`}>
              <summary className="min-h-[60px] text-[15.5px]">
                <span className="font-bold text-slate-900">{f.q}</span>
                <ChevronDown className="lp-chev w-5 h-5 shrink-0 text-slate-400" />
              </summary>
              <p className="m-0 pb-5 text-[15px] leading-relaxed text-slate-600">{f.a}</p>
            </details>
          ))}
        </div>
      </section>
    ),

    final_cta: vis.final_cta && (demoCta ? (
      <section key="final_cta" className="rounded-3xl bg-[#0A0F2B] text-white relative overflow-hidden" data-testid="landing-demo-cta">
        <div className="absolute -top-24 -right-24 w-96 h-96 bg-[#F4C244]/10 rounded-full blur-3xl pointer-events-none" aria-hidden="true" />
        <div className="absolute -bottom-28 -left-20 w-80 h-80 bg-[#F4C244]/15 rounded-full blur-3xl pointer-events-none" aria-hidden="true" />
        <div className={`grid ${demoCta.image ? "lg:grid-cols-2" : ""} gap-10 lg:gap-14 items-center p-8 sm:p-12 relative`}>
          <div className="min-w-0 flex flex-col gap-5">
            <span className="inline-flex items-center gap-2.5 text-[11px] font-extrabold uppercase tracking-[0.22em] text-amber-300" data-testid="landing-demo-cta-eyebrow">
              <span className="w-6 h-[3px] rounded-full bg-[#F4C244]" aria-hidden="true" /> Free demo class
            </span>
            <h2 className="m-0 text-3xl sm:text-4xl font-extrabold tracking-tight leading-[1.1]" data-testid="landing-demo-cta-title">{demoCta.title}</h2>
            {demoCta.desc && <p className="m-0 text-[15px] sm:text-base leading-relaxed text-slate-300 max-w-xl" data-testid="landing-demo-cta-desc">{demoCta.desc}</p>}
            <div className="flex flex-wrap gap-3 mt-1">
              {demoCta.phone && (
                <a href={`tel:${demoCta.phone}`} data-testid="landing-demo-cta-call"
                  className="inline-flex items-center gap-2 min-h-[54px] px-8 bg-[#F4C244] hover:bg-[#ecb733] active:scale-[.98] rounded-full font-extrabold text-base text-slate-900 shadow-[0_10px_24px_rgba(0,0,0,.25)] transition-all">
                  <Phone className="w-[18px] h-[18px]" /> {demoCta.ctaLabel || "Call us now"}
                </a>
              )}
              <a href="#enquire" data-testid="landing-demo-cta-book"
                className="inline-flex items-center gap-2 min-h-[54px] px-7 bg-white/10 border-[1.5px] border-white/30 rounded-full font-extrabold text-base text-white hover:bg-white/20 transition-colors">
                {demoCta.bookLabel || "Book a free demo"} <ArrowRight className="w-[18px] h-[18px]" />
              </a>
            </div>
            {(demoCta.avatars.length > 0 || demoCta.trustedText) && (
              <div className="flex items-center gap-3 mt-1" data-testid="landing-demo-cta-avatars">
                {demoCta.avatars.length > 0 && (
                  <div className="flex -space-x-3">
                    {demoCta.avatars.slice(0, 5).map((src, i) => (
                      <img key={i} src={src} alt="Training Basket learner" loading="lazy"
                        className="w-11 h-11 rounded-full object-cover border-[3px] border-[#0A0F2B] shadow-md" />
                    ))}
                  </div>
                )}
                {demoCta.trustedText && <span className="text-sm font-semibold text-slate-300">{demoCta.trustedText}</span>}
              </div>
            )}
          </div>
          {demoCta.image && (
            <div className="relative min-w-0">
              <img src={demoCta.image} alt="Training Basket demo class" loading="lazy"
                className="w-full rounded-2xl object-cover aspect-[3/2] shadow-[0_24px_60px_-20px_rgba(0,0,0,0.5)] border border-white/20" data-testid="landing-demo-cta-image" />
            </div>
          )}
        </div>
      </section>
    ) : (
      <section key="final_cta" className="rounded-3xl bg-gradient-to-br from-[#F4C244] to-[#EAB308] p-9 sm:p-14 flex flex-wrap gap-8 items-center justify-between relative overflow-hidden" data-testid="landing-final-cta">
        <div className="absolute -bottom-20 -left-20 w-80 h-80 bg-white/20 rounded-full blur-3xl pointer-events-none" aria-hidden="true" />
        <div className="flex flex-col gap-2.5 max-w-xl relative min-w-0">
          <h2 className="m-0 text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">Start your {domainName ? domainName.toLowerCase() : ""} career this month</h2>
          <p className="m-0 text-base font-medium text-[#2A2410]">Limited seats left for the upcoming batch. Get an instant counsellor callback.</p>
          <p className="m-0 text-sm font-semibold text-[#2A2410]/80">
            {[duration, mode, fee, nextBatch && `Next batch ${nextBatch}`].filter(Boolean).join(" · ")}
          </p>
        </div>
        <div className="flex flex-wrap gap-3 relative">
          <a href="#enquire" data-testid="landing-final-enroll"
            className="inline-flex items-center gap-2 min-h-[54px] px-8 bg-slate-900 hover:bg-black active:scale-[.98] rounded-full font-extrabold text-base text-white shadow-xl transition-all">
            Enrol now for next batch <ArrowRight className="w-[18px] h-[18px]" />
          </a>
          <a href={phoneHref} data-testid="landing-final-call"
            className="inline-flex items-center gap-2 min-h-[54px] px-7 bg-white/80 border-[1.5px] border-slate-900/20 rounded-full font-extrabold text-base text-slate-900 hover:bg-white transition-colors">
            <Phone className="w-[18px] h-[18px]" /> Call {phone}
          </a>
        </div>
      </section>
    )),

    also_see: vis.also_see && (
      <section key="also_see" data-testid="landing-also-see" aria-labelledby="lp-alsosee-h">
        <span className="text-xs font-semibold uppercase tracking-widest text-amber-700">Keep exploring</span>
        <h2 id="lp-alsosee-h" className="m-0 mt-1 text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">Also see</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
          {alsoSee.map((o, i) => (
            <Link key={`${o.url_path}-${i}`} to={o.url_path} data-testid={`landing-also-${i}`}
              className="group flex items-center justify-between gap-3 bg-white border border-slate-200/80 border-t-4 border-t-amber-400 rounded-2xl px-6 py-5 font-bold text-slate-800 shadow-sm hover:-translate-y-1 hover:border-amber-400/80 hover:shadow-lg transition-all duration-200">
              <span className="min-w-0">{o.title}</span>
              <ChevronRight className="w-5 h-5 shrink-0 text-slate-300 group-hover:text-amber-600 group-hover:translate-x-0.5 transition-all" />
            </Link>
          ))}
        </div>
      </section>
    ),
  };

  if (lp.url_path === "/contact-us") {
    return <ContactPage lp={lp} />;
  }
  if (lp.url_path === "/corporate-venue") {
    return <VenuePage lp={lp} />;
  }
  if (lp.url_path === "/mission") {
    return <MissionPage lp={lp} />;
  }
  if (lp.url_path === "/testimonials") {
    return <TestimonialsPage lp={lp} />;
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC] overflow-x-clip" data-testid="landing-page">
      <JsonLd data={courseLd} />
      <JsonLd data={faqLd} />
      <style>{`
        .lp-body h2{font-size:1.4rem;font-weight:800;color:#0F172A;letter-spacing:-.01em;margin:1.4rem 0 .6rem}
        .lp-body h3{font-size:1.1rem;font-weight:700;color:#0F172A;margin:1.2rem 0 .4rem}
        .lp-body h4{font-size:1rem;font-weight:700;color:#334155;margin:1.25rem 0 .4rem}
        .lp-body h5{font-size:.95rem;font-weight:700;color:#0F172A;margin:1rem 0 .2rem}
        .lp-slider-track{scroll-snap-type:x mandatory;scrollbar-width:none;-webkit-overflow-scrolling:touch}
        .lp-slider-track::-webkit-scrollbar{display:none}
        .lp-placed{display:flex;gap:.9rem;align-items:center;border:1px solid #e2e8f0;border-radius:1rem;padding:.9rem 1rem;background:#fff}
        .lp-placed img{width:60px;height:60px;border-radius:9999px;object-fit:cover;margin:0;flex-shrink:0;border:2px solid #F1F5F9}
        .lp-placed h5{margin:0;font-size:.95rem;font-weight:800;color:#0F172A}
        .lp-placed p{margin:.15rem 0 0;font-size:.82rem;color:#475569;line-height:1.45}
        .lp-placed .lp-placed-meta{color:#B45309;font-weight:700}
        .lp-placed-slide{flex:0 0 280px;scroll-snap-align:start;min-height:112px;transition:border-color .2s,transform .2s}
        .lp-placed-slide:hover{border-color:#F4C244;transform:translateY(-2px)}
        .lp-body p{color:#475569;line-height:1.75;margin:.6rem 0;font-size:1rem}
        .lp-body ul,.lp-body ol{margin:.6rem 0 .6rem 1.25rem;color:#475569;line-height:1.75}
        .lp-body ul{list-style:disc}.lp-body ol{list-style:decimal}
        .lp-body li{margin:.25rem 0}
        .lp-body a{color:#B45309;font-weight:600;text-decoration:underline;text-underline-offset:2px}
        .lp-body table{width:100%;border-collapse:collapse;margin:1rem 0;font-size:.9rem}
        .lp-body th,.lp-body td{border:1px solid #e2e8f0;padding:.55rem .75rem;text-align:left}
        .lp-body th{background:#f8fafc;font-weight:700;color:#0F172A}
        .lp-body{overflow-wrap:break-word}
        .lp-body-v2>h2{border-left:4px solid #F4C244;padding-left:.85rem;margin-top:2.4rem}
        .lp-body-v2 p strong{background:linear-gradient(transparent 62%,rgba(244,194,68,.32) 62%);padding:0 1px;border-radius:2px;color:#0A0F2B}
        .lp-body-v2 .lp-stats{display:grid;grid-template-columns:repeat(auto-fit,minmax(150px,1fr));gap:.9rem;margin:1.4rem 0}
        .lp-body-v2 .lp-stat{background:#FFFBEB;border:1px solid #FDE68A;border-radius:1rem;padding:1rem .8rem;text-align:center}
        .lp-body-v2 .lp-stat h2{margin:0;font-size:1.3rem;color:#0F172A;overflow-wrap:anywhere}
        .lp-body-v2 .lp-stat p{margin:.3rem 0 0;font-size:.72rem;font-weight:700;letter-spacing:.08em;text-transform:uppercase;color:#B45309}
        .lp-body-v2 .lp-checks{display:grid;grid-template-columns:1fr;gap:.6rem;margin:1.2rem 0}
        @media(min-width:640px){.lp-body-v2 .lp-checks{grid-template-columns:1fr 1fr}}
        .lp-body-v2 .lp-check{display:flex;align-items:center;gap:.6rem;background:#F8FAFC;border:1px solid #EEF2F7;border-radius:.8rem;padding:.6rem .9rem}
        .lp-body-v2 .lp-check img{width:18px;height:18px;max-width:18px;margin:0;flex-shrink:0;border-radius:0}
        .lp-body-v2 .lp-check p,.lp-body-v2 .lp-check h4,.lp-body-v2 .lp-check h5{margin:0;font-size:.92rem;font-weight:600;color:#334155;line-height:1.4}
        .lp-body-v2 ul li::marker,.lp-body-v2 ol li::marker{color:#D9A21B}
        .lp-body-v2 .lp-avatars{display:flex;align-items:center;margin:1.2rem 0}
        .lp-body-v2 .lp-avatars img{width:44px;height:44px;max-width:44px;border-radius:9999px;margin:0;border:2.5px solid #fff;box-shadow:0 1px 5px rgba(15,23,42,.18);object-fit:cover}
        .lp-body-v2 .lp-avatars img+img{margin-left:-12px}
        .lp-body-v2 .lp-avatars p{margin:0 0 0 1rem;font-weight:700;color:#0F172A;font-size:.95rem}
        .lp-body-v2 .lp-list-cols{display:grid;grid-template-columns:repeat(auto-fit,minmax(230px,1fr));gap:.45rem 1.75rem;list-style:none;margin-left:0}
        .lp-body-v2 .lp-list-cols li{margin:0;padding-left:1.4rem;position:relative;font-weight:600;color:#334155;font-size:.95rem}
        .lp-body-v2 .lp-list-cols li::before{content:"";position:absolute;left:0;top:.42em;width:.55rem;height:.55rem;border-radius:9999px;background:#F4C244}
        .lp-body-v2>img,.lp-body-v2>p>img:only-child{max-width:min(440px,100%)!important;display:block}
        .lp-body img{max-width:100%;height:auto;border-radius:.9rem;margin:1rem 0}
        .lp-body blockquote{border-left:3px solid #F4C244;padding:.5rem 0 .5rem 1rem;color:#475569;font-style:italic;margin:1rem 0}
        .lp-acc summary{list-style:none;cursor:pointer;display:flex;align-items:center;justify-content:space-between;gap:12px;font-weight:800}
        .lp-marquee{overflow:hidden;-webkit-mask-image:linear-gradient(90deg,transparent,#000 8%,#000 92%,transparent);mask-image:linear-gradient(90deg,transparent,#000 8%,#000 92%,transparent)}
        .lp-marquee-track{display:flex;width:max-content;animation:lp-marquee 32s linear infinite}
        .lp-marquee:hover .lp-marquee-track,.lp-marquee:focus-within .lp-marquee-track{animation-play-state:paused}
        .lp-marquee-half{display:flex;gap:.75rem;padding-right:.75rem}
        .lp-marquee-item{flex:0 0 auto;height:64px;width:140px;background:#fff;border-radius:.75rem;display:flex;align-items:center;justify-content:center;padding:.6rem;box-shadow:0 2px 10px rgba(0,0,0,.25)}
        .lp-marquee-item--light{border:1px solid #e2e8f0;box-shadow:0 2px 8px rgba(15,23,42,.05)}
        .lp-marquee-item img{max-height:100%;max-width:100%;object-fit:contain}
        @keyframes lp-marquee{to{transform:translateX(-50%)}}
        .lp-acc summary::-webkit-details-marker{display:none}
        .lp-acc .lp-chev{transition:transform .25s}
        .lp-acc details[open] .lp-chev{transform:rotate(180deg)}
        a:focus-visible,button:focus-visible,input:focus-visible,select:focus-visible,summary:focus-visible{outline:none;box-shadow:0 0 0 2px #fff,0 0 0 4px #F59E0B}
        @media (prefers-reduced-motion: reduce){*{transition:none !important;animation:none !important}}
      `}</style>

      {/* Hero + enquiry card */}
      {showHero && (
      <section className="text-slate-900">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-8 pb-14">
          {!heroSecondary && (
          <nav aria-label="Breadcrumb" className="text-sm text-slate-500 flex flex-wrap items-center gap-1.5 mb-8" data-testid="landing-breadcrumb">
            <Link to="/" className="hover:text-amber-700 font-medium transition-colors">Home</Link>
            <ChevronRight className="w-3.5 h-3.5 text-slate-300" aria-hidden="true" />
            {domainName && (
              <>
                <Link to={`/domains/${prog.category_slug}`} className="hover:text-amber-700 font-medium transition-colors" data-testid="landing-breadcrumb-domain">{domainName}</Link>
                <ChevronRight className="w-3.5 h-3.5 text-slate-300" aria-hidden="true" />
              </>
            )}
            <span className="text-slate-900 font-bold">{lp.h1 || lp.title}</span>
          </nav>
          )}

          <div className={`grid lg:grid-cols-12 gap-10 ${heroSparse ? "items-center" : "items-start"}`}>
            <div className="lg:col-span-7 flex flex-col gap-6 min-w-0">
              {lp.badge && (
                <span className="self-start inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-amber-100 text-amber-900 border border-amber-300/60" data-testid="landing-badge">
                  <Sparkles className="w-3.5 h-3.5" /> {lp.badge}
                </span>
              )}
              {!lp.badge && glanceData?.subtitle && (
                <span className="self-start inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-amber-100 text-amber-900 border border-amber-300/60" data-testid="landing-glance-badge">
                  <Sparkles className="w-3.5 h-3.5" /> {glanceData.subtitle}
                </span>
              )}
              {heroSecondary ? (
                <h2 className="m-0 text-3xl sm:text-4xl font-extrabold tracking-tight leading-[1.06] text-slate-900" data-testid="landing-hero-secondary-heading">
                  {h1Main}
                  {h1Accent && <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#D9A21B] to-[#F4C244]">{h1Accent}</span>}
                </h2>
              ) : (
                <h1 className="m-0 text-4xl sm:text-5xl lg:text-[3.4rem] font-extrabold tracking-tight leading-[1.06] text-slate-900" data-testid="landing-h1">
                  {h1Main}
                  {h1Accent && <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#D9A21B] to-[#F4C244]">{h1Accent}</span>}
                </h1>
              )}
              {lp.hero_intro && (
                <p className="m-0 text-lg leading-relaxed text-slate-600 max-w-2xl" data-testid="landing-intro">{lp.hero_intro}</p>
              )}
              {!lp.hero_intro && (glanceData?.intro || overviewIntro) && (
                <p className="m-0 text-lg leading-relaxed text-slate-600 max-w-2xl" data-testid="landing-glance-intro">{glanceData?.intro || overviewIntro}</p>
              )}
              {(glanceData?.highlights || []).length > 0 && (
                <ul className="m-0 grid sm:grid-cols-2 gap-x-6 gap-y-2.5" data-testid="landing-glance-highlights">
                  {glanceData.highlights.map((h, i) => (
                    <li key={i} className="flex items-start gap-2.5 text-[15px] font-semibold text-slate-700" data-testid={`landing-glance-highlight-${i}`}>
                      <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" /> {h}
                    </li>
                  ))}
                </ul>
              )}
              {!heroHasChips && (glanceData?.chips || []).length > 0 && (
                <div className="flex flex-wrap gap-2.5" data-testid="landing-glance-chips">
                  {glanceData.chips.map((c, i) => (
                    <span key={i} className="inline-flex items-center gap-1.5 px-4 py-2 bg-white border border-slate-200 rounded-full text-sm font-semibold text-slate-700" data-testid={`landing-glance-chip-${i}`}>
                      {c.icon && <img src={c.icon} alt="" loading="lazy" className="w-4 h-4 object-contain" />} {c.label}
                    </span>
                  ))}
                </div>
              )}
              {heroHasChips && (
              <div className="flex flex-wrap gap-2.5" data-testid="landing-chips">
                {prog?.placement_text && (
                  <span className="inline-flex items-center gap-1.5 px-4 py-2 bg-white border border-slate-200 rounded-full text-sm font-semibold text-slate-700">
                    <BadgeCheck className="w-4 h-4 text-emerald-600" /> 100% Placement Support
                  </span>
                )}
                {modules.length > 0 && (
                  <span className="inline-flex items-center gap-1.5 px-4 py-2 bg-white border border-slate-200 rounded-full text-sm font-semibold text-slate-700">
                    <FlaskConical className="w-4 h-4 text-amber-600" /> Hands-on Labs
                  </span>
                )}
                {certification && (
                  <span className="inline-flex items-center gap-1.5 px-4 py-2 bg-white border border-slate-200 rounded-full text-sm font-semibold text-slate-700">
                    <Award className="w-4 h-4 text-amber-600" /> Global Certificate
                  </span>
                )}
                {mode && <span className="inline-flex items-center px-4 py-2 bg-white border border-slate-200 rounded-full text-sm font-semibold text-slate-700">{mode}</span>}
                {centreChip && <span className="inline-flex items-center px-4 py-2 bg-white border border-slate-200 rounded-full text-sm font-semibold text-slate-700">{centreChip}</span>}
              </div>
              )}
              <div className="flex flex-wrap items-center gap-4">
                <a href="#enquire" data-testid="landing-enroll-btn"
                  className="inline-flex items-center gap-2 min-h-[54px] px-8 bg-[#F4C244] hover:bg-[#ecb733] active:scale-[.98] rounded-full font-extrabold text-base text-slate-900 shadow-[0_10px_24px_rgba(217,161,27,.35)] transition-all">
                  Enrol now{fee ? ` — ${fee}` : ""} <ArrowRight className="w-[18px] h-[18px]" />
                </a>
                {prog?.syllabus_pdf && (
                  <a href={prog.syllabus_pdf} target="_blank" rel="noreferrer" data-testid="landing-syllabus-btn"
                    className="inline-flex items-center gap-2 min-h-[54px] px-6 bg-white border-[1.5px] border-slate-200 rounded-full font-bold text-base text-slate-900 hover:border-amber-500 transition-colors">
                    <Download className="w-[18px] h-[18px] text-amber-700" /> Download syllabus
                  </a>
                )}
                {glanceData?.cta && !prog?.syllabus_pdf && (
                  <a href="#enquire" data-testid="landing-glance-cta"
                    className="inline-flex items-center gap-2 min-h-[54px] px-6 bg-white border-[1.5px] border-slate-200 rounded-full font-bold text-base text-slate-900 hover:border-amber-500 transition-colors">
                    {glanceData.cta} <ArrowRight className="w-[18px] h-[18px] text-amber-700" />
                  </a>
                )}
              </div>
              {alumniPhotos.length > 0 && (
                <div className="flex items-center gap-3 mt-1">
                  <div className="flex -space-x-3">
                    {alumniPhotos.slice(0, 5).map((src, i) => (
                      <img key={i} src={src} alt="Training Basket graduate" loading="lazy"
                        className="w-11 h-11 rounded-full object-cover border-[3px] border-white shadow-md" />
                    ))}
                  </div>
                  <div className="text-sm">
                    <span className="flex items-center gap-1">
                      <Star className="w-4 h-4 fill-amber-400 text-amber-400" />
                      <strong className="text-slate-900">4.5/5</strong>
                    </span>
                    <span className="text-slate-500 font-medium">10,000+ satisfied learners</span>
                  </div>
                </div>
              )}
              {alumniPhotos.length === 0 && (glanceData?.avatars || []).length >= 3 && (
                <div className="flex items-center gap-3 mt-1" data-testid="landing-glance-avatars">
                  <div className="flex -space-x-3">
                    {glanceData.avatars.slice(0, 5).map((src, i) => (
                      <img key={i} src={src} alt="Training Basket learner" loading="lazy"
                        className="w-11 h-11 rounded-full object-cover border-[3px] border-white shadow-md" />
                    ))}
                  </div>
                  {glanceData.learnersText && (
                    <div className="text-sm">
                      {!glanceHasRating && (
                        <span className="flex items-center gap-1">
                          <Star className="w-4 h-4 fill-amber-400 text-amber-400" />
                          <strong className="text-slate-900">4.5/5</strong>
                        </span>
                      )}
                      <span className="text-slate-500 font-medium">{glanceData.learnersText}</span>
                    </div>
                  )}
                </div>
              )}
              {nextBatch && (
                <p className="m-0 text-sm text-slate-500" data-testid="landing-next-batch">
                  Next batch: <strong className="text-slate-900">{nextBatch}</strong> · Weekday and weekend batches
                </p>
              )}
            </div>

            {/* Enquiry card */}
            <div className="lg:col-span-5 min-w-0">
              <div id="enquire" className="lg:sticky lg:top-6 rounded-3xl p-[1.5px] bg-gradient-to-br from-amber-300 via-amber-200 to-transparent shadow-2xl" data-testid="landing-enquiry">
                {sent ? (
                  <div className="bg-white rounded-3xl p-7 text-center py-10" data-testid="landing-enquiry-success">
                    <CheckCircle2 className="w-12 h-12 text-amber-600 mx-auto" />
                    <h2 className="text-xl font-extrabold text-slate-900 mt-4">Request received!</h2>
                    <p className="text-slate-500 text-sm mt-2">Our counsellor will call you back shortly.</p>
                    <Link to="/thank-you" className="inline-flex items-center gap-2 mt-5 text-amber-700 font-bold text-sm hover:text-amber-800">
                      Continue <ArrowRight className="w-4 h-4" />
                    </Link>
                  </div>
                ) : (
                  <div className="bg-white rounded-3xl p-7">
                    <form onSubmit={submit} className="flex flex-col gap-4" data-testid="landing-enquiry-form">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex flex-col gap-1">
                          <h2 className="m-0 text-[22px] font-extrabold text-slate-900">Get a free counselling call</h2>
                          <p className="m-0 text-sm text-slate-500">Talk to a mentor about batches, fees and career paths.</p>
                        </div>
                        <span className="shrink-0 inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-bold bg-rose-50 text-rose-700 border border-rose-200">
                          <Flame className="w-3.5 h-3.5" /> Limited seats
                        </span>
                      </div>
                      <label className={labelCls}>Full name
                        <span className="relative">
                          <User className={fieldIconCls} />
                          <input required placeholder="Your name" value={form.name}
                            onChange={(e) => setForm({ ...form, name: e.target.value })} className={fieldCls}
                            data-testid="landing-enquiry-name" />
                        </span>
                      </label>
                      <label className={labelCls}>Mobile number
                        <span className="relative">
                          <Phone className={fieldIconCls} />
                          <input required type="tel" placeholder="+91 98XXXXXX21" value={form.phone}
                            onChange={(e) => setForm({ ...form, phone: e.target.value })} className={fieldCls}
                            data-testid="landing-enquiry-phone" />
                        </span>
                      </label>
                      <label className={labelCls}>Email
                        <span className="relative">
                          <Mail className={fieldIconCls} />
                          <input type="email" placeholder="you@example.com" value={form.email}
                            onChange={(e) => setForm({ ...form, email: e.target.value })} className={fieldCls}
                            data-testid="landing-enquiry-email" />
                        </span>
                      </label>
                      <label className={labelCls}>Preferred mode
                        <span className="relative">
                          <MonitorSmartphone className={fieldIconCls} />
                          <select value={form.mode} onChange={(e) => setForm({ ...form, mode: e.target.value })} className={fieldCls}
                            data-testid="landing-enquiry-mode">
                            <option value="">Any mode</option>
                            {cityRec?.has_centre && <option>{`Classroom — ${cityRec.centre_name || lp.city}`}</option>}
                            <option>Live online</option>
                            <option>Blended</option>
                          </select>
                        </span>
                      </label>
                      <button type="submit" disabled={sending}
                        className="min-h-[52px] rounded-full bg-[#F4C244] hover:bg-[#ecb733] active:scale-[.98] text-slate-900 font-extrabold text-base transition-all shadow-md hover:shadow-lg disabled:opacity-60"
                        data-testid="landing-enquiry-submit">
                        {sending ? "Submitting…" : "Request Free Demo & Syllabus"}
                      </button>
                      <p className="m-0 text-[13px] text-slate-500 text-center">
                        Need instant guidance? <a href={phoneHref} className="font-bold text-slate-900 underline underline-offset-2" data-testid="landing-enquiry-call">Talk to a counsellor — {phone}</a>
                      </p>
                    </form>
                  </div>
                )}
              </div>
            </div>
          </div>
          {(glanceData?.stats || []).length >= 2 && (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-10" data-testid="landing-hero-stats">
              {glanceData.stats.map((s, i) => (
                <div key={i} className="bg-[#0A0F2B] border border-[#F4C244]/25 rounded-2xl px-6 py-5 text-center shadow-[0_8px_24px_-6px_rgba(10,15,43,0.25)]" data-testid={`landing-hero-stat-${i}`}>
                  <span className="block text-xl sm:text-2xl font-extrabold tracking-tight text-white">{s.value}</span>
                  <span className="block mt-1 text-[11px] font-bold uppercase tracking-[0.14em] text-[#F4C244]">{s.label}</span>
                </div>
              ))}
            </div>
          )}
          {heroImg && (
            <div className="mt-10" data-testid="landing-hero-image-wrap">
              <img src={heroImg} alt="About Training Basket page preview" loading="eager" fetchpriority="high" className="w-full aspect-video rounded-2xl border border-slate-200 shadow-[0_24px_60px_-20px_rgba(15,23,42,0.25)] object-cover" data-testid="landing-hero-image" />
            </div>
          )}
        </div>
      </section>
      )}

      {/* At a glance — dark KPI strip */}
      {vis.glance && glanceMetrics.length > 0 && (
        <section aria-label="Course at a glance" className="max-w-6xl mx-auto px-4 sm:px-6" data-testid="landing-glance">
          <div className="rounded-2xl bg-slate-900 border border-slate-800 shadow-xl grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 p-5 sm:p-6">
            {glanceMetrics.map(([Icon, label, value]) => (
              <div key={label} className="flex flex-col gap-1.5 px-2" data-testid={`landing-glance-${label.toLowerCase().replace(/\s/g, "-")}`}>
                <span className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-widest text-amber-400">
                  <Icon className="w-4 h-4" /> {label}
                </span>
                <span className="text-lg font-extrabold text-white leading-snug">{value}</span>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* In-page tabs — sticky glass bar */}
      {showTabs && tabs.length > 1 && (
        <div className="sticky top-3 z-40 max-w-6xl mx-auto px-4 sm:px-6 mt-8">
          <nav aria-label="On this page" className="bg-white/90 backdrop-blur-xl border border-slate-200 shadow-sm rounded-full px-3 py-2 flex items-center gap-1 overflow-x-auto" data-testid="landing-tabs">
            {tabs.map(([id, label], i) => (
              <a key={id} href={`#${id}`} data-testid={`landing-tab-${id}`}
                className={`whitespace-nowrap inline-flex items-center min-h-[40px] px-4 rounded-full font-bold text-sm transition-colors ${i === 0 ? "bg-slate-900 text-white" : "text-slate-700 hover:bg-amber-50 hover:text-amber-900"}`}>
                {label}
              </a>
            ))}
            <a href="#enquire" data-testid="landing-quick-apply"
              className="ml-auto whitespace-nowrap inline-flex items-center min-h-[40px] px-5 rounded-full bg-[#F4C244] hover:bg-[#ecb733] font-extrabold text-sm text-slate-900 transition-colors">
              Quick Apply
            </a>
          </nav>
        </div>
      )}

      <main className="max-w-6xl mx-auto px-4 sm:px-6 pb-16 flex flex-col gap-20 mt-14">
        {order.filter((k) => sectionNodes[k]).map((k) => sectionNodes[k])}

        {/* Legacy related-course banner */}
        {lp.related_path && (
          <div className="rounded-2xl border border-amber-200 bg-amber-50 p-5 flex flex-wrap items-center justify-between gap-3" data-testid="landing-related">
            <p className="text-sm text-amber-900 font-medium">
              This is a summer training / internship program. Looking for the full-length course instead?
            </p>
            <Link to={lp.related_path} className="inline-flex items-center gap-1.5 text-sm font-bold text-amber-700 hover:text-amber-900" data-testid="landing-related-link">
              View main course <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        )}
      </main>
    </div>
  );
}
